import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Plus, Users, Trash2, Save, Edit3, Eye, EyeOff,
  Play, Pause, RotateCcw, FastForward, 
  CheckCircle, AlertTriangle, Link2, UserX, Eraser, Check,
  BarChart3, Lightbulb, Clock, Package, Printer,
  ToggleLeft, ToggleRight, Table2, FileText, Download,
  ChevronDown, ChevronUp, ChevronRight, X, Search, FolderOpen, FilePlus,
  Maximize2, Minimize2
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { Button, Badge, Modal, Card } from './ui';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// MAIN PRODUCTION FLOOR COMPONENT
// ============================================================
export function ProductionFloor() {
  const { 
    lines, stations, personnel, projects, shifts,
    categories, customers,
    focusedLineId, setFocusedLineId,
    fetchStations, fetchProjects, fetchShifts, fetchPersonnel,
    fetchCategories, fetchCustomers, fetchSeatTypes,
    prepTables, fetchPrepTables,
    togglePrepTableActive, assignPrepTablePersonnel,
    addPrepTableOperation, deletePrepTableOperation, clearPrepTable,
    addPrepTable, removePrepTable, updatePrepTableOperation,
    lineActiveState, toggleLineActive, isLineActive,
    prepTablesActive, togglePrepTablesActiveState,
    lineResults, setLineResult,
    globalSimState, globalSimSpeed, globalSimTime,
    setGlobalSimState, setGlobalSimSpeed, setGlobalSimTime, resetGlobalSim,
    lineSimCompleted, prepTableSimCompleted, setPrepTableSimCompleted,
    prepTableOvertime, setPrepTableOvertime,
    clearAllCarousels,
  } = useAppStore();
  
  const [showExportModal, setShowExportModal] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const globalTimerRef = useRef(null);
  const globalTimeRef = useRef(0);
  const prepSimRef = useRef({});

  useEffect(() => {
    fetchProjects(); fetchShifts(); fetchPersonnel();
    fetchCategories(); fetchCustomers(); fetchSeatTypes();
    fetchPrepTables(); fetchStations();
  }, []);

  const getLineStations = (lineId) => stations.filter(s => s.line_id === lineId).sort((a, b) => a.station_number - b.station_number);

  const shift = shifts[0];
  const shiftSeconds = shift ? shift.net_work_minutes * 60 : 27000;
  const isSimLocked = globalSimState !== 'idle'; // UI lock during simulation

  // Calculate All Active + Start Global Simulation
  const handleSimulateAll = async () => {
    if (!shift) return;
    
    // 1. FULL RESET first - everything to zero
    handleResetAll();
    await new Promise(r => setTimeout(r, 100)); // Let React flush reset
    
    setCalculating(true);

    let calculated = 0;
    for (const line of lines.filter(l => l.line_type !== 'table')) {
      if (!(lineActiveState[line.id] ?? true)) continue;
      const config = useAppStore.getState().lineConfigs[line.id] || {};
      let projectId = config.selProject;
      if (!projectId) {
        const lineProject = projects.find(p => p.line_id === line.id);
        if (lineProject) projectId = lineProject.id;
      }
      if (!projectId) continue;
      try {
        const res = await axios.post(`${API_URL}/api/calculate-line-production`, {
          line_id: line.id, project_id: projectId, shift_id: shift.id,
          overtime_minutes: config.overtimeMin || 0,
        });
        setLineResult(line.id, { ...res.data, line_code: line.code });
        useAppStore.getState().updateLineConfig(line.id, { productionResult: res.data, selProject: projectId });
        calculated++;
      } catch (err) { console.error(`Error calculating ${line.code}:`, err); }
    }

    setCalculating(false);
    
    // Check for active lines with no personnel (production = 0) - BLOCK simulation
    const warnings = [];
    for (const line of lines.filter(l => l.line_type !== 'table')) {
      if (!(lineActiveState[line.id] ?? true)) continue;
      const result = useAppStore.getState().lineResults[line.id];
      if (result && result.daily_production === 0 && result.message) {
        warnings.push(`• ${line.code}: ${result.message}`);
      }
    }
    // Check prep tables with no personnel
    for (const table of prepTables) {
      if (!table.is_active) continue;
      if (table.total_time_sec > 0 && !(table.assigned_personnel?.length > 0)) {
        warnings.push(`• ${table.name}: No personnel assigned`);
      }
    }
    if (warnings.length > 0) {
      alert(`Cannot start simulation!\n\nThe following active areas have no personnel:\n${warnings.join('\n')}\n\nPlease assign personnel or deactivate these areas.`);
      return; // BLOCK - do not start simulation
    }

    if (calculated === 0 && !prepTables.some(t => t.is_active && t.total_time_sec > 0)) {
      alert('No active lines or prep tables found.');
      return;
    }
    // Start global simulation
    globalTimeRef.current = 0;
    setGlobalSimState('running');
  };

  // Global timer for prep table simulation
  useEffect(() => {
    if (globalSimState !== 'running') {
      if (globalTimerRef.current) clearInterval(globalTimerRef.current);
      return;
    }
    // Calculate max shift time including all overtimes (lines + prep tables)
    const lineOvertimes = lines.filter(l => l.line_type !== 'table').map(l => (useAppStore.getState().lineConfigs[l.id]?.overtimeMin || 0));
    const tableOvertimes = prepTables.map(t => (prepTableOvertime[t.id] || 0));
    const maxOvertime = Math.max(0, ...lineOvertimes, ...tableOvertimes);
    const maxShiftSeconds = shiftSeconds + maxOvertime * 60;

    const TICK_MS = 50;
    globalTimerRef.current = setInterval(() => {
      const speed = useAppStore.getState().globalSimSpeed;
      const dt = speed * (TICK_MS / 1000);
      globalTimeRef.current += dt;

      if (globalTimeRef.current >= maxShiftSeconds) {
        globalTimeRef.current = maxShiftSeconds;
        setGlobalSimTime(maxShiftSeconds);
        setGlobalSimState('finished');
        clearInterval(globalTimerRef.current);
        return;
      }

      setGlobalSimTime(Math.floor(globalTimeRef.current));

      // Prep table production simulation
      const activeTables = prepTables.filter(t => t.is_active && t.total_time_sec > 0 && ((t.assigned_personnel || []).length > 0 || t.assigned_personnel_name));
      for (const table of activeTables) {
        const personnelCount = (table.assigned_personnel || []).length || (table.assigned_personnel_name ? 1 : 0);
        const cycleTime = table.total_time_sec / personnelCount;
        if (cycleTime <= 0) continue;
        const tableMaxTime = shiftSeconds + (prepTableOvertime[table.id] || 0) * 60;
        const effectiveTime = Math.min(globalTimeRef.current, tableMaxTime);
        const completed = Math.floor(effectiveTime / cycleTime);
        if (!prepSimRef.current[table.id]) prepSimRef.current[table.id] = 0;
        if (completed !== prepSimRef.current[table.id]) {
          prepSimRef.current[table.id] = completed;
          setPrepTableSimCompleted(table.id, completed);
        }
      }
    }, TICK_MS);

    return () => { if (globalTimerRef.current) clearInterval(globalTimerRef.current); };
  }, [globalSimState, prepTables, shiftSeconds, prepTableOvertime]);

  const handlePauseAll = () => setGlobalSimState('paused');
  const handleResumeAll = () => setGlobalSimState('running');
  const handleResetAll = () => {
    if (globalTimerRef.current) clearInterval(globalTimerRef.current);
    globalTimeRef.current = 0;
    prepSimRef.current = {};
    resetGlobalSim();
  };

  const totalActiveLines = lines.filter(l => l.line_type !== 'table' && (lineActiveState[l.id] ?? true)).length;
  const isGlobalActive = globalSimState !== 'idle';

  const formatTime = (seconds) => {
    const s = Math.floor(seconds);
    const hrs = Math.floor(s / 3600); const mins = Math.floor((s % 3600) / 60); const secs = s % 60;
    if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6" data-testid="production-floor">
      {/* Top Action Bar */}
      <div className="bg-slate-900 rounded-xl p-3 text-white space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-300">Active: {totalActiveLines} lines | {prepTables.filter(t => t.is_active).length} tables</span>
            {globalSimState === 'idle' && (
              <button onClick={async () => {
                if (window.confirm('Clear all carousel line stations? (Personnel, operations will be removed. Tables are NOT affected.)')) {
                  await clearAllCarousels();
                  // Reset all line configs
                  lines.filter(l => l.line_type !== 'table').forEach(l => {
                    useAppStore.getState().updateLineConfig(l.id, { selProject: '', selCategory: '', selCustomer: '', selSeatType: '', productionResult: null, completedUnits: 0, configModified: false });
                    useAppStore.getState().setLineResult(l.id, null);
                  });
                }
              }} className="px-2.5 py-1 text-[10px] font-semibold bg-red-600/80 hover:bg-red-600 text-white rounded-lg transition-colors" data-testid="clear-all-carousels">
                Clear All Lines
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Speed Control */}
            <div className="flex items-center gap-1">
              <FastForward className="w-3.5 h-3.5 text-slate-400" />
              <select value={globalSimSpeed} onChange={(e) => setGlobalSimSpeed(Number(e.target.value))}
                className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs text-white" data-testid="global-speed">
                <option value={1}>1x</option><option value={10}>10x</option><option value={50}>50x</option>
                <option value={100}>100x</option><option value={500}>500x</option><option value={1000}>1000x</option>
              </select>
            </div>
            {/* Sim Controls */}
            {globalSimState === 'idle' && (
              <Button variant="primary" size="sm" onClick={handleSimulateAll} disabled={calculating} data-testid="calculate-all-btn">
                <Play className="w-4 h-4" /> {calculating ? 'Calculating...' : 'Simulate All Active'}
              </Button>
            )}
            {globalSimState === 'running' && (
              <Button variant="secondary" size="sm" onClick={handlePauseAll} data-testid="pause-all-btn">
                <Pause className="w-4 h-4" /> Pause All
              </Button>
            )}
            {globalSimState === 'paused' && (
              <Button variant="primary" size="sm" onClick={handleResumeAll} data-testid="resume-all-btn">
                <Play className="w-4 h-4" /> Resume All
              </Button>
            )}
            {isGlobalActive && (
              <Button variant="secondary" size="sm" onClick={handleResetAll} data-testid="reset-all-btn">
                <RotateCcw className="w-4 h-4" /> Reset
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} data-testid="export-all-btn">
              <Printer className="w-4 h-4" /> Export
            </Button>
          </div>
        </div>
        {/* Global Sim Progress */}
        {isGlobalActive && (
          <div className="pt-2 border-t border-slate-700">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-4 text-xs">
                <span className="text-slate-400">Time: <span className="font-mono font-bold text-white text-sm">{formatTime(globalSimTime)}</span> / {formatTime(shiftSeconds)}</span>
                <span className="text-slate-400">Lines: <span className="font-mono font-bold text-emerald-400">{Object.values(lineSimCompleted).reduce((s, c) => s + c, 0)}</span> units</span>
                <span className="text-slate-400">Tables: <span className="font-mono font-bold text-violet-400">{Object.values(prepTableSimCompleted).reduce((s, c) => s + c, 0)}</span> units</span>
              </div>
              <div className="flex items-center gap-1">
                {globalSimState === 'running' && <div className="flex items-center gap-1 text-emerald-400"><div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /><span className="text-xs font-bold">RUNNING</span></div>}
                {globalSimState === 'paused' && <span className="text-xs font-bold text-amber-400">PAUSED</span>}
                {globalSimState === 'finished' && <span className="text-xs font-bold text-blue-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> SHIFT COMPLETE</span>}
              </div>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-1.5">
              <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-100" style={{ width: `${Math.min((globalSimTime / shiftSeconds) * 100, 100)}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* All Lines (exclude tables from simulation) */}
      {lines.filter(l => l.line_type !== 'table').map((line) => (
        <LineArea 
          key={line.id}
          line={line}
          lineStations={getLineStations(line.id)}
          isFocused={focusedLineId === line.id}
          onFocus={() => setFocusedLineId(focusedLineId === line.id ? null : line.id)}
          isActive={lineActiveState[line.id] ?? true}
          onToggleActive={() => toggleLineActive(line.id)}
          globalSimState={globalSimState}
          globalSimSpeed={globalSimSpeed}
          isSimLocked={isSimLocked}
        />
      ))}

      {/* Production Tables */}
      <PrepTablesSection 
        prepTables={prepTables}
        isActive={prepTablesActive}
        onToggleActive={togglePrepTablesActiveState}
        personnel={personnel}
        onToggleTableActive={togglePrepTableActive}
        onAssignPersonnel={assignPrepTablePersonnel}
        onAddOperation={addPrepTableOperation}
        onDeleteOperation={deletePrepTableOperation}
        onUpdateOperation={updatePrepTableOperation}
        onClear={clearPrepTable}
        onAddTable={addPrepTable}
        onRemoveTable={removePrepTable}
        isSimLocked={isSimLocked}
        prepTableOvertime={prepTableOvertime}
        setPrepTableOvertime={setPrepTableOvertime}
      />

      {/* Packaging Area */}
      <PackagingArea lineResults={lineResults} lines={lines} prepTables={prepTables} />

      {/* Day-End Summary - ONLY after simulation finishes */}
      {globalSimState === 'finished' && (
        <Card className="border-2 border-cyan-600 bg-cyan-950 text-white" data-testid="multi-line-summary">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-heading font-semibold text-cyan-300">Day-End Production Report</h3>
            <Badge variant="success" className="ml-2">Shift Complete</Badge>
            <button onClick={() => {
              const fmtTime = (s) => { const m = Math.floor(s/60); const sec = s%60; return `${m}:${String(sec).padStart(2,'0')}`; };
              const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
              const timeStr = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
              const totalUnits = Object.values(lineSimCompleted).reduce((s,c)=>s+c,0) + Object.values(prepTableSimCompleted).reduce((s,c)=>s+c,0);

              let lineHTML = '';
              Object.entries(lineResults).forEach(([lid, r]) => {
                const simCount = lineSimCompleted[lid] || 0;
                const actual = simCount || r.daily_production;
                const ls = stations.filter(s => s.line_id === lid).sort((a,b) => a.station_number - b.station_number);
                const active = ls.filter(s => (s.total_time_sec||0) > 0 && s.assigned_personnel_id);
                const maxT = active.length > 0 ? Math.max(...active.map(s=>s.total_time_sec)) : 0;
                const avgT = active.length > 0 ? active.reduce((s,st) => s + st.total_time_sec, 0)/active.length : 0;
                const bal = maxT > 0 ? Math.round((avgT/maxT)*100) : 0;
                const idleT = active.reduce((s,st) => s + (maxT - st.total_time_sec), 0);
                const sorted = [...active].sort((a,b) => b.total_time_sec - a.total_time_sec);
                const top = sorted[0];
                const effColor = r.line_efficiency >= 80 ? '#059669' : r.line_efficiency >= 60 ? '#d97706' : '#dc2626';

                lineHTML += `
                <div style="page-break-inside:avoid;margin-bottom:24px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
                  <div style="background:#1e293b;color:white;padding:12px 16px;display:flex;justify-content:space-between;align-items:center;">
                    <div><span style="font-size:20px;font-weight:800;margin-right:12px;">${r.line_code}</span><span style="font-size:14px;color:#94a3b8;">${r.line_name}</span></div>
                    <div style="font-size:28px;font-weight:800;color:#34d399;">${actual} <span style="font-size:12px;color:#94a3b8;">units</span></div>
                  </div>
                  <div style="padding:12px 16px;">
                    <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
                      <tr>
                        <td style="text-align:center;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;color:#059669;">${r.daily_production}</div><div style="font-size:9px;color:#64748b;">Estimated</div>
                        </td>
                        <td style="text-align:center;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;color:#2563eb;">${actual}</div><div style="font-size:9px;color:#64748b;">Actual</div>
                        </td>
                        <td style="text-align:center;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;">${fmtTime(maxT)}</div><div style="font-size:9px;color:#64748b;">Cycle Time</div>
                        </td>
                        <td style="text-align:center;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;color:${effColor};">${r.line_efficiency}%</div><div style="font-size:9px;color:#64748b;">Efficiency</div>
                        </td>
                        <td style="text-align:center;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;color:#0891b2;">${bal}%</div><div style="font-size:9px;color:#64748b;">Line Balance</div>
                        </td>
                        <td style="text-align:center;padding:8px;background:#fef2f2;border:1px solid #fecaca;width:16.6%;">
                          <div style="font-size:18px;font-weight:700;color:#dc2626;">St.${r.bottleneck?.station_number||'?'}</div><div style="font-size:9px;color:#dc2626;">Bottleneck</div>
                        </td>
                      </tr>
                    </table>
                    <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;">
                      <tr style="background:#f1f5f9;"><th style="padding:4px 8px;text-align:left;border:1px solid #e2e8f0;">Station</th><th style="padding:4px 8px;text-align:left;border:1px solid #e2e8f0;">Personnel</th><th style="padding:4px 8px;text-align:right;border:1px solid #e2e8f0;">Time</th><th style="padding:4px 8px;text-align:right;border:1px solid #e2e8f0;">Idle</th></tr>
                      ${active.map(st => {
                        const idle = maxT - st.total_time_sec;
                        const isBot = st.station_number === r.bottleneck?.station_number;
                        return `<tr style="${isBot?'background:#fef2f2;font-weight:600;':''}"><td style="padding:3px 8px;border:1px solid #e2e8f0;">St.${st.station_number}${isBot?' (bottleneck)':''}</td><td style="padding:3px 8px;border:1px solid #e2e8f0;">${st.assigned_personnel_name||'-'}</td><td style="padding:3px 8px;border:1px solid #e2e8f0;text-align:right;">${fmtTime(st.total_time_sec)}</td><td style="padding:3px 8px;border:1px solid #e2e8f0;text-align:right;color:#94a3b8;">${idle > 0 ? fmtTime(idle) : '-'}</td></tr>`;
                      }).join('')}
                    </table>
                    ${top ? `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 12px;font-size:10px;color:#92400e;">
                      <strong>Suggestion:</strong> Bottleneck St.${top.station_number} (${fmtTime(top.total_time_sec)}) is ${fmtTime(Math.round(top.total_time_sec - avgT))} above avg. Total idle time: ${fmtTime(idleT)}.${bal < 85 ? ` Redistribute work to improve line balance from ${bal}% toward 85%+.` : ''}
                    </div>` : ''}
                  </div>
                </div>`;
              });

              let tableHTML = '';
              const activeTbls = prepTables.filter(t => t.is_active && t.operations?.length > 0);
              if (activeTbls.length > 0) {
                tableHTML = `<div style="page-break-inside:avoid;margin-bottom:24px;">
                  <h2 style="font-size:16px;color:#7c3aed;margin:16px 0 8px;">Production Tables</h2>
                  <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px;">
                  ${activeTbls.map(t => {
                    const completed = prepTableSimCompleted[t.id] || 0;
                    const worker = t.assigned_personnel?.[0]?.name || t.assigned_personnel_name || '-';
                    return `<div style="border:1px solid #ddd6fe;border-radius:8px;padding:12px;">
                      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                        <strong style="color:#7c3aed;">${t.name}</strong>
                        <span style="font-size:20px;font-weight:800;color:#7c3aed;">${completed}</span>
                      </div>
                      <div style="font-size:10px;color:#64748b;margin-bottom:6px;">Worker: ${worker}</div>
                      <table style="width:100%;border-collapse:collapse;font-size:10px;">
                        ${(t.operations||[]).map(op => `<tr><td style="padding:2px 0;border-bottom:1px solid #f1f5f9;">${op.name}</td><td style="padding:2px 4px;text-align:right;border-bottom:1px solid #f1f5f9;color:#64748b;">${fmtTime(op.duration_sec)}</td><td style="padding:2px 0;text-align:right;border-bottom:1px solid #f1f5f9;font-weight:600;color:#7c3aed;">x${completed}</td></tr>`).join('')}
                      </table>
                    </div>`;
                  }).join('')}
                  </div>
                </div>`;
              }

              const w = window.open('', '_blank');
              w.document.write(`<!DOCTYPE html><html><head><title>Day-End Production Report</title>
                <style>@page{size:landscape;margin:12mm;}body{font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;max-width:1100px;margin:0 auto;padding:20px;}
                @media print{button{display:none !important;}}</style></head><body>
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;border-bottom:3px solid #0891b2;padding-bottom:12px;">
                  <div><h1 style="margin:0;font-size:22px;color:#0891b2;">Day-End Production Report</h1><p style="margin:4px 0 0;font-size:12px;color:#64748b;">${dateStr} at ${timeStr}${shift ? ` | ${shift.name} (${shift.start_time} - ${shift.end_time})` : ''}</p></div>
                  <div style="text-align:right;"><span style="font-size:32px;font-weight:800;color:#059669;">${totalUnits}</span><span style="font-size:12px;color:#64748b;margin-left:6px;">total units</span></div>
                </div>
                ${lineHTML}${tableHTML}
                <div style="margin-top:16px;padding:12px 16px;background:#ecfdf5;border:2px solid #059669;border-radius:8px;display:flex;justify-content:space-between;align-items:center;">
                  <strong style="font-size:16px;color:#059669;">TOTAL PRODUCTION</strong>
                  <span style="font-size:28px;font-weight:800;">${totalUnits} units</span>
                </div>
                <div style="margin-top:20px;text-align:center;">
                  <button onclick="window.print()" style="padding:10px 24px;background:#0891b2;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;margin-right:8px;">Print / Save as PDF</button>
                  <button onclick="window.close()" style="padding:10px 24px;background:#64748b;color:white;border:none;border-radius:8px;font-size:14px;cursor:pointer;">Close</button>
                </div>
              </body></html>`);
              w.document.close();
            }} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-cyan-700 hover:bg-cyan-600 text-white text-xs font-semibold rounded-lg transition-colors" data-testid="export-day-end-report">
              <Printer className="w-3.5 h-3.5" /> Export PDF
            </button>
          </div>

          {/* Carousel Lines - Detailed KPI */}
          {Object.entries(lineResults).map(([lid, r]) => {
            const simCount = lineSimCompleted[lid] || 0;
            const actual = simCount || r.daily_production;
            const lineStations = stations.filter(s => s.line_id === lid).sort((a, b) => a.station_number - b.station_number);
            const activeStations = lineStations.filter(s => (s.total_time_sec || 0) > 0 && s.assigned_personnel_id);
            const bottleneckStation = r.bottleneck;
            const avgTime = activeStations.length > 0 ? activeStations.reduce((s, st) => s + st.total_time_sec, 0) / activeStations.length : 0;
            const maxTime = activeStations.length > 0 ? Math.max(...activeStations.map(s => s.total_time_sec)) : 0;
            const minTime = activeStations.length > 0 ? Math.min(...activeStations.map(s => s.total_time_sec)) : 0;
            const balanceEff = maxTime > 0 ? Math.round((avgTime / maxTime) * 100) : 0;
            const idleTime = activeStations.reduce((s, st) => s + (maxTime - st.total_time_sec), 0);
            const potentialGain = maxTime > 0 && activeStations.length > 0 ? Math.floor(shiftSeconds / avgTime) - actual : 0;

            // Find improvable stations
            const sortedByTime = [...activeStations].sort((a, b) => b.total_time_sec - a.total_time_sec);
            const topBottleneck = sortedByTime[0];
            const secondBottleneck = sortedByTime[1];

            return (
              <div key={lid} className="mb-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold text-white bg-blue-600 px-3 py-1 rounded">{r.line_code}</span>
                    <span className="text-sm text-slate-400">{r.line_name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-3xl font-bold text-emerald-400">{actual}</span>
                      <span className="text-sm text-slate-500 ml-1">units</span>
                    </div>
                  </div>
                </div>

                {/* KPI Grid */}
                <div className="grid grid-cols-6 gap-2 mb-3">
                  <div className="p-2 bg-slate-900 rounded text-center">
                    <p className="text-lg font-bold text-emerald-400">{r.daily_production}</p>
                    <p className="text-[9px] text-slate-500">Estimated</p>
                  </div>
                  <div className="p-2 bg-slate-900 rounded text-center">
                    <p className="text-lg font-bold text-blue-400">{actual}</p>
                    <p className="text-[9px] text-slate-500">Actual</p>
                  </div>
                  <div className="p-2 bg-slate-900 rounded text-center">
                    <p className="text-lg font-bold text-white">{formatTime(maxTime)}</p>
                    <p className="text-[9px] text-slate-500">Cycle Time</p>
                  </div>
                  <div className={`p-2 bg-slate-900 rounded text-center ${r.line_efficiency >= 80 ? '' : 'border border-amber-600'}`}>
                    <p className={`text-lg font-bold ${r.line_efficiency >= 80 ? 'text-emerald-400' : r.line_efficiency >= 60 ? 'text-amber-400' : 'text-red-400'}`}>{r.line_efficiency}%</p>
                    <p className="text-[9px] text-slate-500">Efficiency</p>
                  </div>
                  <div className="p-2 bg-slate-900 rounded text-center">
                    <p className="text-lg font-bold text-cyan-400">{balanceEff}%</p>
                    <p className="text-[9px] text-slate-500">Line Balance</p>
                  </div>
                  <div className="p-2 bg-slate-900 rounded text-center border border-red-800">
                    <p className="text-lg font-bold text-red-400">St.{bottleneckStation?.station_number || '?'}</p>
                    <p className="text-[9px] text-red-500">Bottleneck</p>
                  </div>
                </div>

                {/* Station Time Bar Chart */}
                <div className="mb-3">
                  <p className="text-[10px] text-slate-500 font-semibold uppercase mb-1">Station Workload Distribution</p>
                  <div className="flex items-end gap-1 h-16">
                    {activeStations.map(st => {
                      const pct = maxTime > 0 ? (st.total_time_sec / maxTime) * 100 : 0;
                      const isBottleneck = st.station_number === bottleneckStation?.station_number;
                      return (
                        <div key={st.id} className="flex-1 flex flex-col items-center gap-0.5" title={`St.${st.station_number}: ${formatTime(st.total_time_sec)} (${st.assigned_personnel_name})`}>
                          <span className="text-[8px] text-slate-500">{formatTime(st.total_time_sec)}</span>
                          <div className={`w-full rounded-t transition-all ${isBottleneck ? 'bg-red-500' : pct >= 90 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ height: `${Math.max(pct * 0.5, 4)}px` }} />
                          <span className={`text-[8px] ${isBottleneck ? 'text-red-400 font-bold' : 'text-slate-500'}`}>St.{st.station_number}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="p-2 bg-slate-900/80 rounded border border-slate-700">
                  <div className="flex items-center gap-1 mb-1">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                    <p className="text-[10px] text-amber-400 font-bold uppercase">Improvement Suggestions</p>
                  </div>
                  <div className="space-y-1 text-[11px]">
                    {topBottleneck && (
                      <p className="text-slate-300">
                        <span className="text-red-400 font-semibold">Bottleneck St.{topBottleneck.station_number}</span> ({formatTime(topBottleneck.total_time_sec)}) is {formatTime(topBottleneck.total_time_sec - Math.round(avgTime))} above average. 
                        Reducing it by {formatTime(Math.round((topBottleneck.total_time_sec - avgTime) / 2))} could increase output by ~{Math.max(1, Math.floor(potentialGain / 2))} units/day.
                      </p>
                    )}
                    {balanceEff < 85 && (
                      <p className="text-slate-300">
                        <span className="text-cyan-400 font-semibold">Line Balance {balanceEff}%</span> — Total idle time: {formatTime(idleTime)}. 
                        Redistribute {formatTime(Math.round((maxTime - minTime) / 2))} from St.{topBottleneck?.station_number} to St.{sortedByTime[sortedByTime.length-1]?.station_number} for better balance.
                      </p>
                    )}
                    {r.line_efficiency >= 85 && <p className="text-emerald-400">Line is well-balanced. Focus on reducing cycle time for more output.</p>}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Production Tables - Detailed Output */}
          {prepTables.filter(t => t.is_active && t.operations?.length > 0).length > 0 && (
            <div className="mb-4 p-4 bg-violet-900/40 rounded-xl border border-violet-700">
              <div className="flex items-center gap-2 mb-3">
                <Table2 className="w-5 h-5 text-violet-400" />
                <h4 className="font-heading font-semibold text-violet-300">Production Tables Output</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {prepTables.filter(t => t.is_active && t.operations?.length > 0).map(t => {
                  const completed = prepTableSimCompleted[t.id] || 0;
                  const personnel = t.assigned_personnel?.[0]?.name || t.assigned_personnel_name || 'Unassigned';
                  const ot = prepTableOvertime[t.id] || 0;
                  const totalShiftSec = shiftSeconds + (ot * 60);
                  const totalOpTime = t.total_time_sec || 0;
                  const cyclesPerShift = totalOpTime > 0 ? Math.floor(totalShiftSec / totalOpTime) : 0;

                  return (
                    <div key={t.id} className="p-3 bg-slate-800 rounded-lg border border-violet-600/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm text-violet-300">{t.name}</span>
                        <span className="text-xl font-bold text-violet-400">{completed}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mb-2">Worker: {personnel} | Cycles: {cyclesPerShift}{ot > 0 ? ` (+${ot}m OT)` : ''}</p>
                      <div className="space-y-1">
                        {(t.operations || []).map((op, i) => {
                          const opMin = Math.floor(op.duration_sec / 60);
                          const opSec = op.duration_sec % 60;
                          const qtyPerShift = op.duration_sec > 0 ? Math.floor(totalShiftSec / op.duration_sec) : 0;
                          return (
                            <div key={op.id || i} className="flex items-center justify-between text-[10px] py-0.5 border-b border-slate-700/50">
                              <span className="text-slate-300 truncate flex-1">{op.name}</span>
                              <span className="text-slate-500 ml-2 whitespace-nowrap">{opMin}:{String(opSec).padStart(2,'0')}</span>
                              <span className="text-violet-400 font-bold ml-2 whitespace-nowrap">x{completed}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Grand Total */}
          <div className="flex items-center justify-between p-3 bg-emerald-900/60 rounded-xl border-2 border-emerald-500">
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-emerald-300">TOTAL PRODUCTION</span>
              <span className="text-sm text-emerald-500">
                {Object.keys(lineResults).length} lines + {prepTables.filter(t => (prepTableSimCompleted[t.id] || 0) > 0).length} tables
              </span>
            </div>
            <span className="text-3xl font-bold text-white">
              {Object.values(lineSimCompleted).reduce((s, c) => s + c, 0) + Object.values(prepTableSimCompleted).reduce((s, c) => s + c, 0)}
              <span className="text-sm text-emerald-400 ml-2">units</span>
            </span>
          </div>
        </Card>
      )}

      {/* Export Modal */}
      <ExportModal 
        isOpen={showExportModal} 
        onClose={() => setShowExportModal(false)}
        lines={lines}
        stations={stations}
        prepTables={prepTables}
        lineActiveState={lineActiveState}
        prepTablesActive={prepTablesActive}
        lineResults={lineResults}
        personnel={personnel}
      />
    </div>
  );
}

// ============================================================
// LINE AREA COMPONENT
// ============================================================
function LineArea({ line, lineStations, isActive, onToggleActive, globalSimState, globalSimSpeed, isSimLocked }) {
  const { 
    personnel, projects, shifts, categories, customers, seatTypes, personnelAssignments,
    fetchStations, fetchProjects, assignPersonnelToStation, unassignPersonnelFromStation,
    applyProject, saveProjectState, createProjectFromLine,
    updateLineConfig, lineConfigs, setLineResult, lineResults, clearLineResult, setLineSimCompleted,
    clearLine,
  } = useAppStore();

  const config = lineConfigs[line.id] || { selProject: '', selCategory: '', selCustomer: '', overtimeMin: 0, productionResult: null, completedUnits: 0 };
  const setConfig = (updates) => updateLineConfig(line.id, updates);

  const [selectedStation, setSelectedStation] = useState(null);
  const [isStationModalOpen, setIsStationModalOpen] = useState(false);
  const [isPersonnelModalOpen, setIsPersonnelModalOpen] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [simState, setSimState] = useState('idle');
  const [simSpeed, setSimSpeed] = useState(100);
  const [simTime, setSimTime] = useState(0);
  const [completedUnits, setCompletedUnits] = useState(0);
  const [pipelineState, setPipelineState] = useState({});
  const [sharedGroups, setSharedGroups] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveCatId, setSaveCatId] = useState('');
  const [saveCustId, setSaveCustId] = useState('');
  const [saveSeatId, setSaveSeatId] = useState('');

  const simRef = useRef(null);
  const simTimeRef = useRef(0);
  const completedRef = useRef(0);

  // Category → Customer → Seat Type hierarchy
  const selCatName = categories.find(c => c.id === config.selCategory)?.name;
  const selCustName = customers.find(c => c.id === config.selCustomer)?.name;
  const selSeatName = seatTypes.find(s => s.id === config.selSeatType)?.name;

  const filteredCustomers = config.selCategory ? customers.filter(c => c.category_id === config.selCategory) : customers;
  const filteredSeatTypes = config.selCustomer ? seatTypes.filter(s => s.customer_id === config.selCustomer) : seatTypes;
  
  // Projects filtered by hierarchy + line
  const filteredProjects = (() => {
    let p = projects.filter(pr => pr.line_id === line.id);
    if (config.selSeatType) {
      p = p.filter(pr => pr.seat_type_id === config.selSeatType);
    } else {
      if (selCatName) p = p.filter(pr => pr.category_name === selCatName);
      if (selCustName) p = p.filter(pr => pr.customer_name === selCustName);
    }
    return p;
  })();
  
  const selectedProject = config.selProject ? projects.find(p => p.id === config.selProject) : null;
  const hasChanges = config.configModified || false;

  const activeStationKey = lineStations.map(s => `${s.id}:${s.total_time_sec}:${s.assigned_personnel_id}`).join('|');
  const activeStations = useMemo(() => lineStations.filter(s => (s.total_time_sec || 0) > 0 && s.assigned_personnel_id), [activeStationKey]);
  const productionResult = config.productionResult;
  const cycleTime = productionResult ? productionResult.cycle_time_sec : (activeStations.length > 0 ? Math.max(...activeStations.map(s => s.total_time_sec)) : 0);

  const calculateLine = async () => {
    const shift = shifts[0];
    if (!shift || !config.selProject) return;

    // QC validation: C3 (7-st) → St.7 must be active; C1/C2 (20-st) → at least one of St.16-20 must be active
    if (line.station_count === 7) {
      const st7 = lineStations.find(s => s.station_number === 7);
      if (!st7 || !st7.total_time_sec || !st7.assigned_personnel_id) {
        alert('Station 7 (QC & Packaging) must have operations and personnel assigned before calculating.');
        return;
      }
    } else if (line.station_count === 20) {
      const qcStations = lineStations.filter(s => s.station_number >= 16 && s.station_number <= 20);
      const hasActiveQC = qcStations.some(s => s.total_time_sec > 0 && s.assigned_personnel_id);
      if (!hasActiveQC) {
        alert('At least one QC & Packaging station (St.16-20) must have operations and personnel assigned before calculating.');
        return;
      }
    }

    try {
      const res = await axios.post(`${API_URL}/api/calculate-line-production`, {
        line_id: line.id, project_id: config.selProject, shift_id: shift.id,
        overtime_minutes: config.overtimeMin || 0,
      });
      setConfig({ productionResult: res.data });
      setSharedGroups(res.data.shared_personnel_groups || []);
      setLineResult(line.id, { ...res.data, line_code: line.code });
      simStatesRef.current = null;
      simTimeRef.current = 0; completedRef.current = 0;
      setSimTime(0); setCompletedUnits(0); setPipelineState({});
      setSimState('running');
    } catch (err) { console.error(err); }
  };

  const simStatesRef = useRef(null);

  const resetSimulation = () => {
    if (simRef.current) clearInterval(simRef.current);
    simTimeRef.current = 0; completedRef.current = 0;
    simStatesRef.current = null;
    setSimState('idle'); setSimTime(0); setCompletedUnits(0);
    setPipelineState({}); setSharedGroups([]);
  };

  // Store active config in refs for stable interval access
  const activeStationsRef = useRef(activeStations);
  const sharedGroupsRef = useRef(sharedGroups);
  const shiftsRef = useRef(shifts);
  const configRef = useRef(config);
  useEffect(() => { activeStationsRef.current = activeStations; }, [activeStations]);
  useEffect(() => { sharedGroupsRef.current = sharedGroups; }, [sharedGroups]);
  useEffect(() => { shiftsRef.current = shifts; }, [shifts]);
  useEffect(() => { configRef.current = config; }, [config]);

  // Realistic event-driven simulation: each station works at its own pace
  useEffect(() => {
    if (simState !== 'running') {
      if (simRef.current) clearInterval(simRef.current);
      return;
    }
    if (!activeStationsRef.current.length) { setSimState('finished'); return; }

    const shift = shiftsRef.current[0];
    const maxTime = shift ? (shift.net_work_minutes + (configRef.current.overtimeMin || 0)) * 60 : 27000;
    const TICK_MS = 50;

    // Shared personnel lookup
    const sharedLookup = {};
    const sharedGroupsList = [];
    for (const g of sharedGroupsRef.current) {
      const gObj = { stations: g.station_numbers, combinedTime: g.combined_time_sec, personnelName: g.personnel_name };
      sharedGroupsList.push(gObj);
      for (const sn of g.station_numbers) sharedLookup[sn] = gObj;
    }

    const sortedStations = [...activeStationsRef.current].sort((a, b) => a.station_number - b.station_number);
    const stationNumbers = sortedStations.map(s => s.station_number);
    const stationTimeMap = {};
    for (const s of sortedStations) stationTimeMap[s.station_number] = s.total_time_sec;

    // All station numbers for this line (including empty ones, for transit paths)
    const allStationNumbers = lineStations.map(s => s.station_number).sort((a, b) => a - b);
    const TRANSIT_TIME_PER_STATION = 2; // 2 seconds per empty station (walking speed)

    // Initialize simulation state (only once per run)
    if (!simStatesRef.current) {
      const states = {};
      for (const sn of stationNumbers) {
        states[sn] = { status: 'idle', productId: null, timeWorked: 0, totalTime: stationTimeMap[sn] };
      }
      const groupStates = {};
      for (const g of sharedGroupsList) {
        groupStates[g.stations.join('-')] = { status: 'idle', productId: null, currentSubIdx: 0, timeWorkedInSub: 0 };
      }
      simStatesRef.current = { stationStates: states, groupStates, nextProductId: 1, completed: 0, transits: [] };
    }

    const sim = simStatesRef.current;
    const ss = sim.stationStates;

    const isInGroup = (sn) => !!sharedLookup[sn];
    const isFirstInGroup = (sn) => sharedLookup[sn]?.stations[0] === sn;
    const isLastInGroup = (sn) => { const g = sharedLookup[sn]; return g?.stations[g.stations.length - 1] === sn; };
    const getGroupKey = (sn) => sharedLookup[sn]?.stations.join('-');
    const getNextSn = (sn) => { const i = stationNumbers.indexOf(sn); return i < stationNumbers.length - 1 ? stationNumbers[i + 1] : null; };

    simRef.current = setInterval(() => {
      const effectiveSpeed = globalSimState !== 'idle' ? useAppStore.getState().globalSimSpeed : simSpeed;
      const dt = effectiveSpeed * (TICK_MS / 1000);
      simTimeRef.current += dt;
      if (simTimeRef.current >= maxTime) {
        simTimeRef.current = maxTime; setSimTime(maxTime);
        completedRef.current = sim.completed; setCompletedUnits(sim.completed); setLineSimCompleted(line.id, sim.completed);
        setSimState('finished'); clearInterval(simRef.current); return;
      }

      // === PHASE 0: Process transits (products moving through empty stations) ===
      for (let ti = sim.transits.length - 1; ti >= 0; ti--) {
        const tr = sim.transits[ti];
        tr.timeInStation += dt;
        if (tr.timeInStation >= TRANSIT_TIME_PER_STATION) {
          tr.timeInStation = 0;
          tr.pathIdx++;
          if (tr.pathIdx >= tr.path.length) {
            // Transit complete - deliver to target station
            const targetSn = tr.targetSn;
            let canDeliver = false;
            if (targetSn === null) {
              // Going to output (last station was active, product complete)
              sim.completed++;
              sim.transits.splice(ti, 1);
              continue;
            }
            if (isInGroup(targetSn)) {
              canDeliver = isFirstInGroup(targetSn) && sim.groupStates[getGroupKey(targetSn)].status === 'idle';
            } else { canDeliver = ss[targetSn].status === 'idle'; }
            if (canDeliver) {
              if (isInGroup(targetSn)) {
                const gk = getGroupKey(targetSn); const gs = sim.groupStates[gk];
                gs.status = 'working'; gs.productId = tr.productId; gs.currentSubIdx = 0; gs.timeWorkedInSub = 0;
                ss[sharedLookup[targetSn].stations[0]].status = 'working'; ss[sharedLookup[targetSn].stations[0]].productId = tr.productId; ss[sharedLookup[targetSn].stations[0]].timeWorked = 0;
              } else { ss[targetSn].status = 'working'; ss[targetSn].productId = tr.productId; ss[targetSn].timeWorked = 0; }
              sim.transits.splice(ti, 1);
            }
            // If can't deliver, wait (pathIdx stays at end, product waits at last empty station)
          }
        }
      }

      // Helper: get empty stations between two station numbers
      const getTransitPath = (fromSn, toSn) => {
        const fromIdx = allStationNumbers.indexOf(fromSn);
        const toIdx = toSn !== null ? allStationNumbers.indexOf(toSn) : allStationNumbers.length;
        const path = [];
        for (let i = fromIdx + 1; i < toIdx; i++) {
          const sn = allStationNumbers[i];
          if (!stationNumbers.includes(sn)) path.push(sn); // only empty (non-active) stations
        }
        return path;
      };

      // === PHASE 1: Move completed products forward (last → first) ===
      for (let i = stationNumbers.length - 1; i >= 0; i--) {
        const sn = stationNumbers[i];
        if (isInGroup(sn) && !isLastInGroup(sn)) continue;
        if (ss[sn].status !== 'blocked') continue;

        const nextSn = getNextSn(sn);
        const pid = ss[sn].productId;
        const label = `PRD${pid}`;
        const transitPath = getTransitPath(sn, nextSn);

        if (nextSn === null && transitPath.length === 0) {
          // Last active station, no empty stations after → product complete
          sim.completed++;
          if (isInGroup(sn)) {
            const gk = getGroupKey(sn); sim.groupStates[gk].status = 'idle'; sim.groupStates[gk].currentSubIdx = 0;
            for (const gsn of sharedLookup[sn].stations) { ss[gsn].status = 'idle'; ss[gsn].productId = null; ss[gsn].timeWorked = 0; }
          } else { ss[sn].status = 'idle'; ss[sn].productId = null; ss[sn].timeWorked = 0; }
        } else if (transitPath.length > 0) {
          // There are empty stations in between - start transit
          // Free current station
          if (isInGroup(sn)) {
            const gk = getGroupKey(sn); sim.groupStates[gk].status = 'idle'; sim.groupStates[gk].currentSubIdx = 0;
            for (const gsn of sharedLookup[sn].stations) { ss[gsn].status = 'idle'; ss[gsn].productId = null; ss[gsn].timeWorked = 0; }
          } else { ss[sn].status = 'idle'; ss[sn].productId = null; ss[sn].timeWorked = 0; }
          // Create transit
          sim.transits.push({ productId: pid, label, path: transitPath, pathIdx: 0, timeInStation: 0, targetSn: nextSn });
        } else {
          // No empty stations between, direct move (original logic)
          let canAccept = false;
          if (isInGroup(nextSn)) {
            canAccept = isFirstInGroup(nextSn) && sim.groupStates[getGroupKey(nextSn)].status === 'idle';
          } else { canAccept = ss[nextSn].status === 'idle'; }

          if (canAccept) {
            if (isInGroup(sn)) {
              const gk = getGroupKey(sn); sim.groupStates[gk].status = 'idle'; sim.groupStates[gk].currentSubIdx = 0;
              for (const gsn of sharedLookup[sn].stations) { ss[gsn].status = 'idle'; ss[gsn].productId = null; ss[gsn].timeWorked = 0; }
            } else { ss[sn].status = 'idle'; ss[sn].productId = null; ss[sn].timeWorked = 0; }
            if (isInGroup(nextSn)) {
              const gk = getGroupKey(nextSn); const gs = sim.groupStates[gk];
              gs.status = 'working'; gs.productId = pid; gs.currentSubIdx = 0; gs.timeWorkedInSub = 0;
              ss[sharedLookup[nextSn].stations[0]].status = 'working'; ss[sharedLookup[nextSn].stations[0]].productId = pid; ss[sharedLookup[nextSn].stations[0]].timeWorked = 0;
            } else { ss[nextSn].status = 'working'; ss[nextSn].productId = pid; ss[nextSn].timeWorked = 0; }
          }
        }
      }

      // === PHASE 2: Work on products ===
      // Non-shared stations
      for (const sn of stationNumbers) {
        if (isInGroup(sn)) continue;
        if (ss[sn].status === 'working') {
          ss[sn].timeWorked += dt;
          if (ss[sn].timeWorked >= ss[sn].totalTime) { ss[sn].timeWorked = ss[sn].totalTime; ss[sn].status = 'blocked'; }
        }
      }
      // Shared groups
      for (const g of sharedGroupsList) {
        const gk = g.stations.join('-'); const gs = sim.groupStates[gk];
        if (gs.status !== 'working') continue;
        const curSn = g.stations[gs.currentSubIdx];
        ss[curSn].timeWorked += dt;
        if (ss[curSn].timeWorked >= ss[curSn].totalTime) {
          ss[curSn].timeWorked = ss[curSn].totalTime;
          if (gs.currentSubIdx < g.stations.length - 1) {
            // Move worker to next sub-station within group
            ss[curSn].status = 'idle'; ss[curSn].productId = null; ss[curSn].timeWorked = 0;
            gs.currentSubIdx++;
            const nextSn = g.stations[gs.currentSubIdx];
            ss[nextSn].status = 'working'; ss[nextSn].productId = gs.productId; ss[nextSn].timeWorked = 0;
          } else {
            // All sub-stations done → group blocked
            ss[curSn].status = 'blocked'; gs.status = 'blocked';
          }
        }
      }

      // === PHASE 3: Feed first station ===
      const firstSn = stationNumbers[0];
      if (ss[firstSn].status === 'idle') {
        if (isInGroup(firstSn)) {
          const gk = getGroupKey(firstSn); const gs = sim.groupStates[gk];
          if (gs.status === 'idle') {
            const pid = sim.nextProductId++;
            gs.status = 'working'; gs.productId = pid; gs.currentSubIdx = 0; gs.timeWorkedInSub = 0;
            ss[firstSn].status = 'working'; ss[firstSn].productId = pid; ss[firstSn].timeWorked = 0;
          }
        } else {
          const pid = sim.nextProductId++;
          ss[firstSn].status = 'working'; ss[firstSn].productId = pid; ss[firstSn].timeWorked = 0;
        }
      }

      // === Build visual state ===
      const newPS = {};
      for (const station of sortedStations) {
        const sn = station.station_number;
        const st = ss[sn];
        let workerHere = st.status !== 'idle';
        if (isInGroup(sn)) {
          const gs = sim.groupStates[getGroupKey(sn)];
          workerHere = gs.status === 'working' && sharedLookup[sn].stations[gs.currentSubIdx] === sn;
        }
        newPS[sn] = {
          hasProduct: st.status !== 'idle',
          progress: st.totalTime > 0 ? (Math.min(st.timeWorked, st.totalTime) / st.totalTime) * 100 : 0,
          working: st.status === 'working',
          productId: st.productId || 0,
          productLabel: st.productId ? `PRD${st.productId}` : '',
          isShared: isInGroup(sn),
          workerHere,
        };
      }
      // Add transit products to empty stations
      for (const tr of sim.transits) {
        if (tr.pathIdx < tr.path.length) {
          const emptySn = tr.path[tr.pathIdx];
          newPS[emptySn] = { inTransit: true, transitLabel: tr.label };
        } else if (tr.path.length > 0) {
          // Waiting at last empty station before target
          const lastEmpty = tr.path[tr.path.length - 1];
          newPS[lastEmpty] = { inTransit: true, transitLabel: tr.label };
        }
      }
      setPipelineState(newPS);
      setSimTime(Math.floor(simTimeRef.current));
      if (sim.completed !== completedRef.current) { completedRef.current = sim.completed; setCompletedUnits(sim.completed); setLineSimCompleted(line.id, sim.completed); }
    }, TICK_MS);

    return () => { if (simRef.current) clearInterval(simRef.current); };
  }, [simState]);

  // React to global simulation state changes
  useEffect(() => {
    if (globalSimState === 'running' && simState === 'idle') {
      // Global sim started - auto-start this line if it has a production result
      if (config.productionResult && activeStations.length > 0) {
        setSharedGroups(config.productionResult.shared_personnel_groups || []);
        simStatesRef.current = null;
        simTimeRef.current = 0; completedRef.current = 0;
        setSimTime(0); setCompletedUnits(0); setPipelineState({});
        setSimState('running');
      }
    } else if (globalSimState === 'paused' && simState === 'running') {
      setSimState('paused');
    } else if (globalSimState === 'running' && simState === 'paused') {
      setSimState('running');
    } else if (globalSimState === 'idle' && simState !== 'idle') {
      // Global reset
      resetSimulation();
    }
  }, [globalSimState]);

  const handleStationClick = (station) => { setSelectedStation(station); setIsStationModalOpen(true); };

  const refreshStationData = async () => {
    await fetchStations(line.id);
    if (selectedStation) {
      const res = await axios.get(`${API_URL}/api/stations?line_id=${line.id}`);
      const updated = res.data.find(s => s.id === selectedStation.id);
      if (updated) setSelectedStation(updated);
    }
  };

  const handleAssignPersonnel = async (personnelId) => {
    if (!selectedStation) return;
    try {
      await assignPersonnelToStation(selectedStation.id, personnelId);
      await refreshStationData();
      setIsPersonnelModalOpen(false);
      setConfig({ configModified: true });
      // Auto-save personnel to project if one is selected
      if (config.selProject) {
        await saveProjectState(config.selProject);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) alert(detail);
      else console.error(err);
    }
  };

  const formatTime = (seconds) => {
    const s = Math.floor(seconds);
    const hrs = Math.floor(s / 3600); const mins = Math.floor((s % 3600) / 60); const secs = s % 60;
    if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const [editingLeader, setEditingLeader] = useState(false);
  const [leaderName, setLeaderName] = useState(line.production_leader || '');

  const shift = shifts[0];
  const shiftSeconds = shift ? (shift.net_work_minutes + (config.overtimeMin || 0)) * 60 : 27000;
  const isSimActive = simState === 'running' || simState === 'paused' || simState === 'finished';
  const isLocked = isSimActive || isSimLocked;

  return (
    <div className={cn("border-2 rounded-xl transition-all",
      !isActive && "opacity-30 border-slate-200",
      isActive && "border-slate-300 bg-white"
    )} data-testid={`line-area-${line.code}`}>
      {/* Line Header */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-t-xl border-b">
        <button onClick={isSimLocked ? undefined : onToggleActive} className="p-0.5" data-testid={`toggle-line-${line.code}`}
          title={isActive ? 'Deactivate' : 'Activate'} disabled={isSimLocked}>
          {isActive ? <ToggleRight className="w-6 h-6 text-emerald-500" /> : <ToggleLeft className="w-6 h-6 text-slate-400" />}
        </button>
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-lg text-slate-800">{line.code}</span>
          <span className="text-sm text-slate-500">{line.name} ({line.station_count} St)</span>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1" data-testid={`leader-field-${line.code}`}>
            <span className="text-[10px] font-semibold text-teal-600 uppercase">Leader:</span>
            {editingLeader && !isSimLocked ? (
              <select value={leaderName} onChange={e => { setLeaderName(e.target.value); setEditingLeader(false); axios.put(`${API_URL}/api/lines/${line.id}/leader`, { production_leader: e.target.value }); }}
                onBlur={() => setEditingLeader(false)}
                className="border border-teal-300 rounded px-1.5 py-0.5 text-xs bg-white" autoFocus data-testid={`leader-select-${line.code}`}>
                <option value="">-- Select --</option>
                {personnel.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            ) : (
              <button onClick={isSimLocked ? undefined : () => setEditingLeader(true)} className="flex items-center gap-0.5 text-xs text-teal-700 hover:text-teal-500" data-testid={`leader-edit-${line.code}`} disabled={isSimLocked}>
                {leaderName || <span className="text-teal-400 italic text-[10px]">Set</span>}
                {!isSimLocked && <Edit3 className="w-2.5 h-2.5 text-teal-400" />}
              </button>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-slate-200 rounded">
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Line Content - always visible */}
      {!collapsed && isActive && (
        <div className="p-3 space-y-3">
          {/* Controls bar */}
          <Card className="bg-slate-900 text-white border-slate-700">
            <div className="space-y-2">
              {/* Row 1: Category → Customer → Seat Type → Project → Save */}
              <div className="flex items-center gap-2 flex-wrap">
                <select value={config.selCategory || ''} onChange={(e) => setConfig({ selCategory: e.target.value, selCustomer: '', selSeatType: '', selProject: '', configModified: false })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs" data-testid={`category-${line.code}`} disabled={isLocked}>
                  <option value="">Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronRight className="w-3 h-3 text-slate-500" />
                <select value={config.selCustomer || ''} onChange={(e) => setConfig({ selCustomer: e.target.value, selSeatType: '', selProject: '', configModified: false })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs" data-testid={`customer-${line.code}`} disabled={isLocked || !config.selCategory}>
                  <option value="">Customer</option>
                  {filteredCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronRight className="w-3 h-3 text-slate-500" />
                <select value={config.selSeatType || ''} onChange={(e) => setConfig({ selSeatType: e.target.value, selProject: '', configModified: false })}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs" data-testid={`seattype-${line.code}`} disabled={isLocked || !config.selCustomer}>
                  <option value="">Seat Type</option>
                  {filteredSeatTypes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ChevronRight className="w-3 h-3 text-slate-500" />
                <select value={config.selProject || ''} 
                  onChange={async (e) => {
                    const projId = e.target.value;
                    if (!projId) { setConfig({ selProject: '', configModified: false }); return; }
                    try {
                      // Save current project state before switching
                      if (config.selProject) {
                        await saveProjectState(config.selProject);
                      }
                      await applyProject(projId);
                      // Auto-fill hierarchy from project data
                      const proj = projects.find(p => p.id === projId);
                      const updates = { selProject: projId, configModified: false };
                      if (proj) {
                        const cat = categories.find(c => c.name === proj.category_name);
                        const cust = customers.find(c => c.name === proj.customer_name);
                        const st = seatTypes.find(s => s.id === proj.seat_type_id);
                        if (cat) updates.selCategory = cat.id;
                        if (cust) updates.selCustomer = cust.id;
                        if (st) updates.selSeatType = st.id;
                      }
                      setConfig(updates);
                    } catch (err) { console.error(err); }
                  }}
                  className="bg-slate-800 border border-indigo-500 rounded px-2 py-1.5 text-xs min-w-[140px] text-indigo-300"
                  data-testid={`project-select-${line.code}`} disabled={isLocked}>
                  <option value="">-- Projects --</option>
                  {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Button variant="secondary" size="sm" 
                  onClick={() => {
                    if (selectedProject) {
                      // Project loaded: show overwrite vs save-as-new dialog
                      setShowOverwriteDialog(true);
                    } else {
                      // No project: go straight to save-as-new
                      setSaveCatId(config.selCategory || '');
                      setSaveCustId(config.selCustomer || '');
                      setSaveSeatId(config.selSeatType || '');
                      setSaveName('');
                      setShowSaveDialog(true);
                    }
                  }}
                  className="border-emerald-600 text-emerald-300 hover:bg-emerald-900/50" data-testid={`save-${line.code}`} disabled={isLocked}>
                  <Save className="w-3 h-3" /> Save
                </Button>
              </div>
              {/* Save new project dialog */}
              {showSaveDialog && (
                <div className="p-3 bg-slate-800 rounded border border-emerald-600 space-y-2">
                  <p className="text-xs text-emerald-400 font-semibold">Save as New Project</p>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={saveCatId} onChange={e => { setSaveCatId(e.target.value); setSaveCustId(''); setSaveSeatId(''); }}
                      className="bg-slate-700 border border-slate-500 rounded px-2 py-1.5 text-xs text-white" data-testid={`save-cat-${line.code}`}>
                      <option value="">Category *</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={saveCustId} onChange={e => { setSaveCustId(e.target.value); setSaveSeatId(''); }}
                      className="bg-slate-700 border border-slate-500 rounded px-2 py-1.5 text-xs text-white" data-testid={`save-cust-${line.code}`}>
                      <option value="">Customer *</option>
                      {(saveCatId ? customers.filter(c => c.category_id === saveCatId) : customers).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={saveSeatId} onChange={e => setSaveSeatId(e.target.value)}
                      className="bg-slate-700 border border-slate-500 rounded px-2 py-1.5 text-xs text-white" data-testid={`save-seat-${line.code}`}>
                      <option value="">Seat Type *</option>
                      {(saveCustId ? seatTypes.filter(s => s.customer_id === saveCustId) : seatTypes).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="text" placeholder="Project name..." value={saveName} onChange={e => setSaveName(e.target.value)}
                      className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-500 rounded text-xs text-white" data-testid={`save-name-${line.code}`} autoFocus />
                    {!saveName && saveCatId && (
                      <button onClick={() => {
                        const cn = categories.find(c => c.id === saveCatId)?.name || '';
                        const cusn = customers.find(c => c.id === saveCustId)?.name || '';
                        const stn = seatTypes.find(s => s.id === saveSeatId)?.name || '';
                        setSaveName([cn, cusn, stn].filter(Boolean).join(' - '));
                      }}
                        className="text-[10px] text-emerald-400 hover:text-emerald-300 whitespace-nowrap">Auto</button>
                    )}
                    <Button variant="primary" size="sm" disabled={!saveName || !saveCatId || !saveCustId || !saveSeatId} onClick={async () => {
                      try {
                        const result = await createProjectFromLine({
                          name: saveName, line_id: line.id, seat_type_id: saveSeatId,
                        });
                        if (result?.id) {
                          setConfig({
                            selProject: result.id, selCategory: saveCatId, selCustomer: saveCustId, selSeatType: saveSeatId,
                            configModified: false,
                          });
                        }
                        setShowSaveDialog(false); setSaveName('');
                      } catch (err) { console.error(err); }
                    }} data-testid={`save-confirm-${line.code}`}><Save className="w-3 h-3" /> Save</Button>
                    <button onClick={() => { setShowSaveDialog(false); setSaveName(''); }} className="text-slate-400 hover:text-white text-xs">X</button>
                  </div>
                  {(!saveCatId || !saveCustId || !saveSeatId) && (
                    <p className="text-[10px] text-amber-400">* Category, Customer, and Seat Type are required</p>
                  )}
                </div>
              )}
              {/* Overwrite vs Save As New dialog */}
              {showOverwriteDialog && selectedProject && (
                <div className="p-3 bg-slate-800 rounded border border-indigo-500 space-y-2">
                  <p className="text-xs text-indigo-300 font-semibold">Project: {selectedProject.name}</p>
                  <div className="flex items-center gap-3">
                    <Button variant="primary" size="sm" onClick={async () => {
                      await saveProjectState(selectedProject.id);
                      setConfig({ configModified: false });
                      setShowOverwriteDialog(false);
                    }} data-testid={`overwrite-btn-${line.code}`}>
                      <Save className="w-3 h-3" /> Overwrite
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => {
                      setShowOverwriteDialog(false);
                      setSaveCatId(config.selCategory || '');
                      setSaveCustId(config.selCustomer || '');
                      setSaveSeatId(config.selSeatType || '');
                      setSaveName('');
                      setShowSaveDialog(true);
                    }} data-testid={`saveas-btn-${line.code}`}>
                      <FilePlus className="w-3 h-3" /> Save as New
                    </Button>
                    <button onClick={() => setShowOverwriteDialog(false)} className="text-slate-400 hover:text-white text-xs ml-auto">X</button>
                  </div>
                </div>
              )}
              {/* Row 2: OT, Speed, Calculate, Clear - HIDDEN during global simulation */}
              {!isSimLocked && (
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-amber-400" />
                  <select value={config.overtimeMin || 0} onChange={e => setConfig({ overtimeMin: Number(e.target.value) })}
                    className="bg-slate-800 border border-amber-600 rounded px-2 py-1.5 text-xs text-amber-300" data-testid={`overtime-${line.code}`}>
                    <option value={0}>OT: 0</option><option value={30}>OT: 30dk</option><option value={60}>OT: 1h</option>
                    <option value={90}>OT: 1.5h</option><option value={120}>OT: 2h</option><option value={180}>OT: 3h</option>
                  </select>
                  <FastForward className="w-3 h-3 text-slate-400" />
                  <select value={simSpeed} onChange={(e) => setSimSpeed(Number(e.target.value))}
                    className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-xs">
                    <option value={1}>1x</option><option value={10}>10x</option><option value={50}>50x</option>
                    <option value={100}>100x</option><option value={500}>500x</option><option value={1000}>1000x</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {simState === 'idle' && <Button variant="success" size="sm" onClick={calculateLine} disabled={!config.selProject}
                    title={!config.selProject ? 'Select a config first' : ''} data-testid={`sim-start-${line.code}`}>
                    <Play className="w-3 h-3" /> Calculate & Simulate
                  </Button>}
                  {simState === 'running' && <Button variant="secondary" size="sm" onClick={() => setSimState('paused')}><Pause className="w-3 h-3" /> Pause</Button>}
                  {simState === 'paused' && <Button variant="success" size="sm" onClick={() => setSimState('running')}><Play className="w-3 h-3" /> Resume</Button>}
                  {isSimActive && <Button variant="secondary" size="sm" onClick={resetSimulation}><RotateCcw className="w-3 h-3" /> Reset</Button>}
                  <Button variant="secondary" size="sm" onClick={async () => {
                    if (!window.confirm('Clear all operations and personnel from this line?')) return;
                    try {
                      await clearLine(line.id);
                      resetSimulation(); clearLineResult(line.id);
                      setConfig({ selProject: '', selCategory: '', selCustomer: '', selSeatType: '', productionResult: null, configModified: false });
                    } catch (err) { console.error(err); }
                  }} data-testid={`clear-${line.code}`}><Eraser className="w-3 h-3" /> Clear</Button>
                </div>
              </div>
              )}
            </div>

            {/* Sim progress */}
            {isSimActive && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-slate-400">Time: <span className="font-mono font-bold text-white text-sm">{formatTime(simTime)}</span> / {formatTime(shiftSeconds)}</span>
                    <span className="text-slate-400">Cycle: <span className="font-mono font-bold text-amber-400">{formatTime(cycleTime)}</span></span>
                    <span className="text-slate-400">Completed: <span className="font-mono font-bold text-emerald-400 text-lg">{completedUnits}</span></span>
                  </div>
                  <div className="flex items-center gap-1">
                    {simState === 'running' && <div className="flex items-center gap-1 text-emerald-400"><div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" /><span className="text-xs">RUNNING</span></div>}
                    {simState === 'paused' && <span className="text-xs text-amber-400">PAUSED</span>}
                    {simState === 'finished' && <span className="text-xs text-blue-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> DONE</span>}
                  </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-100" style={{ width: `${Math.min((simTime / shiftSeconds) * 100, 100)}%` }} />
                </div>
              </div>
            )}
          </Card>

          {/* No project selected notice */}
          {!config.selProject && simState === 'idle' && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4" /> <span className="font-semibold">No project loaded</span> - Select Category → Customer → Seat Type → Project, or start fresh and Save.
            </div>
          )}

          {/* Shared groups warning */}
          {sharedGroups.length > 0 && isSimActive && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs">
              <div className="flex items-center gap-1 mb-1"><Link2 className="w-3 h-3 text-amber-600" /><span className="font-semibold text-amber-700">Shared Personnel</span></div>
              {sharedGroups.map((g, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="text-amber-700 font-medium">{g.personnel_name}:</span>
                  <span className="font-mono">{g.station_numbers.map(sn => `St.${sn}`).join('+')}</span>
                  <span className="text-red-600 font-bold">= {formatTime(g.combined_time_sec)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          {simState === 'finished' && productionResult && (
            <Card className="border-emerald-500 bg-emerald-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading font-semibold text-emerald-800 flex items-center gap-2"><CheckCircle className="w-5 h-5" /> Shift Complete</h4>
                <Badge variant="success" className="text-sm px-3">{completedUnits} UNITS</Badge>
              </div>
              <div className="grid grid-cols-5 gap-3 text-center">
                <div className="p-2 bg-white rounded"><p className="text-xl font-bold text-emerald-600">{productionResult.daily_production}</p><p className="text-[10px] text-slate-500">Est/Day</p></div>
                <div className="p-2 bg-white rounded"><p className="text-xl font-bold text-blue-600">{completedUnits}</p><p className="text-[10px] text-slate-500">Actual</p></div>
                <div className="p-2 bg-white rounded"><p className="text-lg font-bold">{formatTime(cycleTime)}</p><p className="text-[10px] text-slate-500">Cycle</p></div>
                <div className="p-2 bg-white rounded"><p className="text-lg font-bold">{productionResult.line_efficiency}%</p><p className="text-[10px] text-slate-500">Efficiency</p></div>
                <div className="p-2 bg-white rounded border border-red-300"><p className="text-lg font-bold text-red-600">{productionResult.bottleneck?.is_shared ? productionResult.bottleneck.shared_stations.map(s => `St.${s}`).join('+') : `St.${productionResult.bottleneck?.station_number}`}</p><p className="text-[10px] text-red-500">Bottleneck</p></div>
              </div>
            </Card>
          )}

          {/* Station Grid */}
          <CarouselLine 
            line={line} stations={lineStations} onStationClick={isLocked ? null : handleStationClick}
            selectedStation={selectedStation} isSimActive={isSimActive || isSimLocked}
            pipelineState={pipelineState} isFocused={true}
            sharedGroups={sharedGroups} selProject={config.selProject}
          />
        </div>
      )}

      {/* Station Modal */}
      <StationModal 
        isOpen={isStationModalOpen} 
        onClose={() => setIsStationModalOpen(false)}
        station={selectedStation} 
        onOpenPersonnelModal={() => setIsPersonnelModalOpen(true)}
        onUnassignPersonnel={async () => { 
          if (selectedStation) { 
            await unassignPersonnelFromStation(selectedStation.id); 
            await refreshStationData(); 
            setConfig({ configModified: true });
            if (config.selProject) await saveProjectState(config.selProject);
          } 
        }}
        onRefresh={refreshStationData} 
        onModified={() => setConfig({ configModified: true })}
        selProject={config.selProject}
        lineId={line.id}
      />

      {/* Personnel Modal */}
      <Modal isOpen={isPersonnelModalOpen} onClose={() => { setIsPersonnelModalOpen(false); setPersonnelSearch(''); }} title="Assign Personnel">
        <div className="mb-3">
          <input type="text" placeholder="Search by name..." value={personnelSearch} onChange={e => setPersonnelSearch(e.target.value)}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="personnel-search-input" autoFocus />
        </div>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {personnel.filter(p => p.name.toLowerCase().includes(personnelSearch.toLowerCase())).map((person) => {
            const assignment = personnelAssignments[person.id];
            const isOnThisLine = assignment?.type === 'line' && assignment?.line_id === line.id;
            const isOnOtherLine = assignment?.type === 'line' && assignment?.line_id !== line.id;
            const isOnTable = assignment?.type === 'table';
            const isBlocked = isOnOtherLine || isOnTable;
            let badge = null;
            if (isOnThisLine) badge = <span className="ml-auto text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">This line: St.{assignment.stations?.join(', St.')}</span>;
            else if (isOnOtherLine) badge = <span className="ml-auto text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded">{assignment.line} St.{assignment.stations?.join(',')}</span>;
            else if (isOnTable) badge = <span className="ml-auto text-[10px] bg-orange-600 text-white px-1.5 py-0.5 rounded">{assignment.table}</span>;
            return (
              <Button key={person.id} variant="secondary" className={`w-full justify-start text-left ${isBlocked ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={isBlocked}
                onClick={() => { if (!isBlocked) { handleAssignPersonnel(person.id); setPersonnelSearch(''); } }}>
                <Users className="w-4 h-4 mr-2" /><span className="font-medium">{person.name}</span>
                <span className="ml-2 text-slate-500">({person.employee_id})</span>
                {badge}
              </Button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// CAROUSEL LINE (station grid) - U-shaped carousel layout
// ============================================================
function CarouselLine({ line, stations, onStationClick, selectedStation, isSimActive, pipelineState, isFocused, sharedGroups, selProject }) {
  const stationCount = line.station_count;
  const isSmallLine = stationCount <= 10;
  const maxTime = Math.max(...stations.map(s => s.total_time_sec || 0), 0);
  const sharedStationNums = new Set();
  for (const g of (sharedGroups || [])) g.station_numbers.forEach(sn => sharedStationNums.add(sn));

  // Build grid cells for 7-station carousel: 7 cols x 2 rows
  // Top:    [empty][St1][empty][St2][empty][St3][empty]
  // Bottom: [St7]  [empty][St6][empty][St5][empty][St4]
  const buildSmallLineGrid = () => {
    const stMap = {};
    stations.forEach(s => { stMap[s.station_number] = s; });
    const topCells = [null, stMap[1], null, stMap[2], null, stMap[3], null];
    const bottomCells = [stMap[7], null, stMap[6], null, stMap[5], null, stMap[4]];
    return { topCells, bottomCells, cols: 7 };
  };

  // 20-station: 10 cols, top St1-St10, bottom St20-St11 (reversed)
  const buildLargeLineGrid = () => {
    const cols = Math.floor(stationCount / 2);
    const topCells = stations.slice(0, cols).map(s => s);
    const bottomCells = [...stations.slice(cols)].reverse().map(s => s);
    return { topCells, bottomCells, cols };
  };

  const { topCells, bottomCells, cols } = isSmallLine ? buildSmallLineGrid() : buildLargeLineGrid();

  const renderCell = (cell, idx, isBottom) => {
    if (!cell) {
      return <div key={`gap-${idx}`} className="bg-slate-700/20 rounded" style={{ minHeight: isSmallLine ? '90px' : undefined }} />;
    }
    return (
      <StationBox key={cell.id} station={cell} onClick={onStationClick ? () => onStationClick(cell) : null}
        isSelected={selectedStation?.id === cell.id}
        isBottleneck={cell.total_time_sec === maxTime && maxTime > 0}
        isSimActive={isSimActive} pState={pipelineState[cell.station_number]}
        isFocused={isFocused}
        isPackaging={isSmallLine ? cell.station_number === stationCount : cell.station_number > stationCount - 5}
        reverseProgress={isBottom}
        isSharedStation={sharedStationNums.has(cell.station_number)}
        selProject={selProject}
        isSmallLine={isSmallLine} />
    );
  };

  return (
    <div data-testid={`carousel-${line.code}`}>
      <div className={cn("bg-slate-800 border-2 border-red-700 p-2", isSmallLine ? "rounded-[40px]" : "rounded-2xl")}>
        {/* Top Row */}
        <div className="grid gap-1.5 mb-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {topCells.map((cell, i) => renderCell(cell, i, false))}
        </div>
        {/* Bottom Row */}
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {bottomCells.map((cell, i) => renderCell(cell, i, true))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STATION BOX - Industrial dark theme with LED bar
// ============================================================
function StationBox({ station, onClick, isSelected, isBottleneck, isSimActive, pState, isFocused, isPackaging, reverseProgress, isSharedStation, selProject, isSmallLine }) {
  const stationTime = station.total_time_sec || 0;
  const hasProduct = pState?.hasProduct || false;
  const progressPct = pState?.progress || 0;
  const isWorking = pState?.working && isSimActive;
  const workerHere = pState?.workerHere ?? true;
  const hasPersonnel = !!station.assigned_personnel_id;
  const mins = Math.floor(stationTime / 60);
  const secs = stationTime % 60;
  const showProjectData = !!selProject;

  const getLEDColor = () => {
    if (!showProjectData) return hasPersonnel ? 'bg-slate-500' : 'bg-slate-600';
    if (pState?.inTransit && isSimActive && isFocused) return 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]';
    if (!hasPersonnel && stationTime > 0) return 'bg-red-800';
    if (isSimActive && isFocused) {
      if (isWorking && hasProduct && workerHere) return 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.6)]';
      if (hasProduct && !workerHere) return 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]';
      if (hasProduct && !isWorking) return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]';
      return 'bg-slate-600';
    }
    if (isBottleneck && stationTime > 0) return 'bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.6)]';
    if (stationTime > 0 && hasPersonnel) return 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]';
    return 'bg-slate-600';
  };

  const getBorderColor = () => {
    if (!showProjectData) return 'border-slate-600';
    if (!hasPersonnel && stationTime > 0) return 'border-red-700';
    if (isBottleneck && stationTime > 0) return 'border-red-500';
    if (isSharedStation) return 'border-amber-500';
    if (isPackaging || station.is_qc) return 'border-amber-500';
    return 'border-slate-600';
  };

  return (
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onClick}
      className={cn('rounded-lg cursor-pointer transition-all relative overflow-hidden border-2',
        getBorderColor(), isSelected && 'ring-4 ring-yellow-400 ring-offset-2 ring-offset-slate-800',
        !hasPersonnel && !pState?.inTransit && 'opacity-30')}
      style={isSmallLine ? { minHeight: '90px' } : undefined}
      data-testid={`station-${station.station_number}`}>
      <div className={cn('h-2 w-full transition-all duration-200', getLEDColor())} />
      <div className="bg-slate-700 p-1.5 h-full flex flex-col justify-between">
        {/* Header */}
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-white font-bold text-[11px]">
            St.{station.station_number}
            {station.is_qc && <span className="text-amber-400 ml-0.5 text-[8px]">QC</span>}
            {isPackaging && <span className="text-amber-400 ml-0.5 text-[8px]">PKG</span>}
          </span>
          <div className="flex items-center gap-0.5">
            {showProjectData && isSharedStation && <Link2 className="w-3 h-3 text-amber-400" />}
            {showProjectData && isBottleneck && stationTime > 0 && <AlertTriangle className="w-3 h-3 text-red-400" />}
          </div>
        </div>

        {/* Person icon + name */}
        <div className="flex items-center gap-1 mb-0.5">
          {hasPersonnel ? (
            <AnimatePresence mode="wait">
              {(!isSimActive || !isSharedStation || workerHere) && (
                <motion.div key="worker-here" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
                  className="flex items-center gap-1">
                  <div className={cn("w-4 h-4 rounded-full flex items-center justify-center",
                    isSimActive && isFocused && workerHere ? "bg-emerald-500" : "bg-slate-500")}>
                    <User className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[9px] text-slate-300 truncate">{station.assigned_personnel_name?.split(' ')[0]}</span>
                </motion.div>
              )}
              {isSimActive && isSharedStation && !workerHere && (
                <motion.div key="worker-away" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center bg-slate-600 border border-dashed border-blue-400">
                    <User className="w-2.5 h-2.5 text-blue-400 opacity-40" />
                  </div>
                  <span className="text-[8px] text-blue-400">away</span>
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full flex items-center justify-center bg-red-900">
                <UserX className="w-2.5 h-2.5 text-red-400" />
              </div>
              <span className="text-[9px] text-red-400">empty</span>
            </div>
          )}
        </div>

        {/* Total time - always show */}
        <div className="font-mono text-slate-300 text-right text-[11px] font-semibold mt-auto">
          {stationTime > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : '--:--'}
        </div>

        {/* Product label badge (PRD1, PRD2...) */}
        <AnimatePresence>
          {hasProduct && isSimActive && isFocused && pState?.productLabel && (
            <motion.div initial={{ scale: 0, y: -5 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0, y: 5 }}
              className="absolute right-0.5 top-2.5">
              <div className={cn("px-1 py-0.5 rounded text-[8px] font-bold border shadow-lg",
                isWorking && workerHere ? "bg-purple-600 border-purple-400 text-white" :
                !workerHere ? "bg-blue-900 border-blue-500 text-blue-300" :
                "bg-amber-700 border-amber-400 text-white")}>
                {pState.productLabel}
              </div>
            </motion.div>
          )}
          {/* Transit product - cart passing through empty station */}
          {pState?.inTransit && isSimActive && isFocused && (
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 20, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute right-0.5 top-2.5">
              <div className="px-1 py-0.5 rounded text-[8px] font-bold border shadow-lg bg-cyan-700 border-cyan-400 text-white animate-pulse">
                {pState.transitLabel}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar */}
        {isSimActive && isFocused && stationTime > 0 && hasProduct && (
          <div className={cn("absolute bottom-0 left-0 right-0 h-1.5 bg-slate-900", reverseProgress && "[transform:scaleX(-1)]")}>
            <div className={cn("h-full transition-[width] duration-[50ms]",
              isWorking && workerHere ? "bg-emerald-400" : !workerHere ? "bg-blue-400/30" : "bg-amber-400")}
              style={{ width: `${Math.min(progressPct, 100)}%` }} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================
// STATION MODAL
// ============================================================
function StationModal({ isOpen, onClose, station, onOpenPersonnelModal, onUnassignPersonnel, onRefresh, onModified, selProject, lineId }) {
  const [editingOp, setEditingOp] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDuration, setEditDuration] = useState(0);
  const [newOpName, setNewOpName] = useState('');
  const [newOpDuration, setNewOpDuration] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const handleSaveOp = async (opId) => {
    if (!window.confirm('Save changes to this operation?')) return;
    await axios.put(`${API_URL}/api/operations/${opId}/quick-update`, { name: editName, duration_sec: editDuration });
    setEditingOp(null); await onRefresh(); onModified?.();
  };
  const handleDeleteOp = async (opId) => {
    if (!window.confirm('Delete this operation?')) return;
    await axios.delete(`${API_URL}/api/operations/${opId}/quick-delete`); await onRefresh(); onModified?.();
  };
  const handleAddOp = async () => {
    if (!newOpName || newOpDuration <= 0 || !station) return;
    await axios.post(`${API_URL}/api/operations/quick-create`, {
      name: newOpName, duration_sec: newOpDuration, project_id: selProject || null, station_id: station.id,
    });
    setNewOpName(''); setNewOpDuration(0); setIsAdding(false); await onRefresh(); onModified?.();
  };

  if (!station) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Station ${station.station_number}${station.is_qc ? ` (QC${station.qc_number})` : ''}`}>
      <div className="space-y-4">
        {!selProject && (
          <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700" data-testid="template-mode-badge">
            No project selected - operations saved without project
          </div>
        )}
        {/* Personnel */}
        <div className="p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-slate-700 flex items-center gap-2"><User className="w-4 h-4" /> Personnel</h4>
            {station.assigned_personnel_id
              ? <Button variant="danger" size="sm" onClick={onUnassignPersonnel} data-testid="unassign-personnel-btn">Remove</Button>
              : <Button variant="secondary" size="sm" onClick={onOpenPersonnelModal} data-testid="assign-personnel-btn"><Plus className="w-3 h-3" /> Assign</Button>}
          </div>
          {station.assigned_personnel_name
            ? <p className="text-lg font-semibold text-emerald-600">{station.assigned_personnel_name}</p>
            : <p className="text-sm text-red-500">No personnel assigned</p>}
        </div>
        {/* Operations */}
        <div className="p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-slate-700">
              Operations ({station.assigned_operations?.length || 0}) - {Math.floor(station.total_time_sec / 60)}m {station.total_time_sec % 60}s
            </h4>
            <Button variant="secondary" size="sm" onClick={() => setIsAdding(true)} data-testid="add-operation-btn"><Plus className="w-3 h-3" /> Add</Button>
          </div>
          {isAdding && (
            <div className="mb-3 p-3 border-2 border-blue-400 rounded-lg bg-white space-y-2">
              <input type="text" placeholder="Operation name" value={newOpName} onChange={(e) => setNewOpName(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="new-op-name" />
              <div className="flex items-center gap-2">
                <input type="number" placeholder="Duration (sec)" value={newOpDuration || ''} onChange={(e) => setNewOpDuration(parseInt(e.target.value) || 0)} className="flex-1 border rounded px-3 py-2 text-sm" data-testid="new-op-duration" />
                <Button variant="success" size="sm" onClick={handleAddOp}><Save className="w-3 h-3" /> Save</Button>
                <Button variant="secondary" size="sm" onClick={() => { setIsAdding(false); setNewOpName(''); setNewOpDuration(0); }}>Cancel</Button>
              </div>
            </div>
          )}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {station.assigned_operations?.length > 0 ? station.assigned_operations.map((op, idx) => (
              <div key={op.id || idx} className="flex items-center gap-2 p-2 bg-white rounded border-l-4 border-blue-500">
                {editingOp === op.id ? (
                  <>
                    <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">{idx + 1}</span>
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 border rounded px-2 py-1 text-sm" />
                    <input type="number" value={editDuration} onChange={(e) => setEditDuration(parseInt(e.target.value) || 0)} className="w-20 border rounded px-2 py-1 text-sm text-right" />
                    <span className="text-xs text-slate-500">s</span>
                    <button onClick={() => handleSaveOp(op.id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Save className="w-4 h-4" /></button>
                    <button onClick={() => setEditingOp(null)} className="p-1 text-slate-400 hover:bg-slate-50 rounded"><RotateCcw className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">{idx + 1}</span>
                    <span className="flex-1 text-sm font-medium">{op.name}</span>
                    <span className="text-sm font-mono text-slate-600">{op.duration_sec}s</span>
                    <button onClick={() => { setEditingOp(op.id); setEditName(op.name); setEditDuration(op.duration_sec); }} className="p-1 text-blue-500 hover:bg-blue-50 rounded"><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDeleteOp(op.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            )) : <p className="text-sm text-slate-500 py-2">No operations assigned</p>}
          </div>
        </div>
        {/* Operations are synced to project automatically */}
      </div>
    </Modal>
  );
}

// ============================================================
// PRODUCTION TABLES SECTION
// ============================================================
function PrepTablesSection({ prepTables, isActive, onToggleActive, personnel, onToggleTableActive, onAssignPersonnel, onAddOperation, onDeleteOperation, onUpdateOperation, onClear, onAddTable, onRemoveTable, isSimLocked, prepTableOvertime, setPrepTableOvertime }) {
  const [selectedTable, setSelectedTable] = useState(null);
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [addingOpFor, setAddingOpFor] = useState(null);
  const [newOpName, setNewOpName] = useState('');
  const [newOpDuration, setNewOpDuration] = useState(0);
  const [editingOp, setEditingOp] = useState(null);
  const [editingName, setEditingName] = useState(null); // { tableId, name }
  const { personnelAssignments, renamePrepTable } = useAppStore();

  const exportTable = (table) => {
    const lines = [];
    lines.push(table.name);
    lines.push('='.repeat(40));
    const pp = table.assigned_personnel || [];
    if (pp.length > 0) lines.push(`Personnel: ${pp.map(p => p.name).join(', ')}`);
    else if (table.assigned_personnel_name) lines.push(`Personnel: ${table.assigned_personnel_name}`);
    else lines.push('Personnel: (unassigned)');
    lines.push('');
    if (table.operations?.length > 0) {
      lines.push('Operations:');
      table.operations.forEach((op, i) => {
        const mins = Math.floor(op.duration_sec / 60);
        const secs = op.duration_sec % 60;
        lines.push(`  ${i + 1}. ${op.name} — ${mins}:${secs.toString().padStart(2, '0')}`);
      });
    }
    const totalMin = Math.floor((table.total_time_sec || 0) / 60);
    const totalSec = (table.total_time_sec || 0) % 60;
    lines.push(`\nTotal Time: ${totalMin}:${totalSec.toString().padStart(2, '0')}`);
    const ot = prepTableOvertime[table.id] || 0;
    if (ot > 0) lines.push(`Overtime: ${ot} min`);
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${table.name.replace(/\s+/g, '_')}_export.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn("border-2 rounded-xl transition-all", isActive ? "border-violet-300 bg-white" : "border-slate-200 bg-slate-50 opacity-30")} data-testid="prep-tables-section">
      <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-t-xl border-b border-violet-200">
        <button onClick={isSimLocked ? undefined : onToggleActive} className="p-0.5" data-testid="toggle-prep-tables" disabled={isSimLocked}>
          {isActive ? <ToggleRight className="w-6 h-6 text-emerald-500" /> : <ToggleLeft className="w-6 h-6 text-slate-400" />}
        </button>
        <Table2 className="w-5 h-5 text-violet-500" />
        <h3 className="font-heading font-bold text-lg text-violet-800">Production Tables</h3>
        <span className="text-xs text-violet-400">{isActive ? 'Active' : 'Inactive'}</span>
        {isSimLocked && <span className="ml-auto text-xs text-amber-500 font-bold">LOCKED - Simulation in progress</span>}
        {isActive && !isSimLocked && (
          <>
            <button onClick={() => {
              const allActive = prepTables.every(t => t.is_active);
              prepTables.forEach(t => { if (t.is_active === allActive) onToggleTableActive(t.id); });
            }} className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-violet-600 hover:bg-violet-100 rounded transition-colors" title={prepTables.every(t => t.is_active) ? 'Turn All Off' : 'Turn All On'}>
              {prepTables.every(t => t.is_active) ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              {prepTables.every(t => t.is_active) ? 'All Off' : 'All On'}
            </button>
            <button onClick={onAddTable} className="flex items-center gap-1 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:bg-violet-700 transition-colors" data-testid="add-prep-table">
              <Plus className="w-3.5 h-3.5" /> Add Table
            </button>
          </>
        )}
      </div>

      {isActive && (
        <div className="p-3 flex flex-wrap gap-3">
          {prepTables.map((table) => (
            <div key={table.id}
              className={cn("border-2 rounded-lg p-3 transition-all min-w-[200px] flex-1 max-w-[280px]",
                table.is_active ? "border-violet-300 bg-white" : "border-slate-200 bg-slate-50 opacity-40")}
              data-testid={`prep-table-${table.table_number}`}>
              <div className="flex items-center justify-between mb-2">
                {editingName?.tableId === table.id ? (
                  <div className="flex items-center gap-1">
                    <input value={editingName.name} onChange={e => setEditingName({...editingName, name: e.target.value})}
                      className="border border-violet-300 rounded px-1.5 py-0.5 text-xs font-bold text-violet-700 w-32" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') { renamePrepTable(table.id, editingName.name); setEditingName(null); } if (e.key === 'Escape') setEditingName(null); }} />
                    <button onClick={() => { renamePrepTable(table.id, editingName.name); setEditingName(null); }} className="text-emerald-600"><Check className="w-3 h-3" /></button>
                    <button onClick={() => setEditingName(null)} className="text-slate-400"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <button onClick={() => !isSimLocked && setEditingName({ tableId: table.id, name: table.name })} 
                    className="font-bold text-sm text-violet-700 hover:text-violet-900 flex items-center gap-1" disabled={isSimLocked}>
                    {table.name} {!isSimLocked && <Edit3 className="w-2.5 h-2.5 opacity-30" />}
                  </button>
                )}
                <div className="flex items-center gap-1">
                  <button onClick={() => exportTable(table)} className="p-0.5 text-slate-400 hover:text-violet-600" title="Export" data-testid={`export-prep-table-${table.table_number}`}>
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={isSimLocked ? undefined : () => onToggleTableActive(table.id)} className="p-0.5" data-testid={`toggle-prep-table-${table.table_number}`} disabled={isSimLocked}>
                    {table.is_active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
                  </button>
                  {!isSimLocked && (
                    <button onClick={() => { if (window.confirm(`Delete ${table.name}?`)) onRemoveTable(table.id); }} className="p-0.5 text-red-400 hover:text-red-600" data-testid={`delete-prep-table-${table.table_number}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mb-2 space-y-1">
                {(table.assigned_personnel || []).length > 0 ? (
                  (table.assigned_personnel || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-1">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center"><User className="w-3 h-3 text-emerald-600" /></div>
                      <span className="text-xs font-medium text-slate-700 truncate">{p.name}</span>
                      <button onClick={() => { if (!isSimLocked && window.confirm(`Remove ${p.name}?`)) onAssignPersonnel(table.id, p.id, 'remove'); }} className="ml-auto p-0.5 text-red-400" disabled={isSimLocked}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))
                ) : table.assigned_personnel_name ? (
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center"><User className="w-3 h-3 text-emerald-600" /></div>
                    <span className="text-xs font-medium text-slate-700 truncate">{table.assigned_personnel_name}</span>
                    <button onClick={() => { if (window.confirm('Remove personnel?')) onAssignPersonnel(table.id, null); }} className="ml-auto p-0.5 text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ) : null}
                {((table.assigned_personnel || []).length < 3 && !(table.assigned_personnel_name && (table.assigned_personnel || []).length === 0)) && !isSimLocked && (
                  <button onClick={() => { setSelectedTable(table); setPersonnelModalOpen(true); }}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-violet-600 w-full" data-testid={`assign-prep-personnel-${table.table_number}`}>
                    <Plus className="w-3 h-3" /> {(table.assigned_personnel || []).length > 0 ? 'Add more' : 'Assign personnel'}
                    {(table.assigned_personnel || []).length > 0 && <span className="ml-auto text-[10px] text-violet-400">{(table.assigned_personnel || []).length}/3</span>}
                  </button>
                )}
              </div>
              <div className="space-y-1 mb-2">
                {table.operations?.map((op, i) => (
                  editingOp && editingOp.tableId === table.id && editingOp.opId === op.id ? (
                    <div key={op.id || i} className="flex items-center gap-1 bg-violet-100 rounded px-1.5 py-1">
                      <input type="text" value={editingOp.name} onChange={e => setEditingOp({ ...editingOp, name: e.target.value })}
                        className="flex-1 border rounded px-1 py-0.5 text-[10px] min-w-0" autoFocus />
                      <input type="number" value={editingOp.duration_sec || ''} onChange={e => setEditingOp({ ...editingOp, duration_sec: parseInt(e.target.value) || 0 })}
                        className="w-14 border rounded px-1 py-0.5 text-[10px] text-right" />
                      <button onClick={async () => {
                        await onUpdateOperation(table.id, op.id, { name: editingOp.name, duration_sec: editingOp.duration_sec });
                        setEditingOp(null);
                      }} className="text-emerald-600"><CheckCircle className="w-3 h-3" /></button>
                      <button onClick={() => setEditingOp(null)} className="text-slate-400"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <div key={op.id || i} className="flex items-center gap-1 text-[10px] bg-violet-50 rounded px-1.5 py-0.5 group">
                      <span className="flex-1 truncate font-medium">{op.name}</span>
                      <span className="font-mono text-violet-600">{op.duration_sec}s</span>
                      {!isSimLocked && (
                        <button onClick={() => setEditingOp({ tableId: table.id, opId: op.id, name: op.name, duration_sec: op.duration_sec })}
                          className="text-slate-300 group-hover:text-violet-500 transition-colors" data-testid={`edit-op-${table.table_number}-${i}`}><Edit3 className="w-2.5 h-2.5" /></button>
                      )}
                      <button onClick={() => { if (!isSimLocked && window.confirm('Delete this operation?')) onDeleteOperation(table.id, op.id); }} className="text-red-400" disabled={isSimLocked}><Trash2 className="w-2.5 h-2.5" /></button>
                    </div>
                  )
                ))}
              </div>
              {!isSimLocked && (addingOpFor === table.id ? (
                <div className="space-y-1">
                  <input type="text" placeholder="Op name" value={newOpName} onChange={e => setNewOpName(e.target.value)} className="w-full border rounded px-2 py-1 text-xs" />
                  <div className="flex gap-1">
                    <input type="number" placeholder="sec" value={newOpDuration || ''} onChange={e => setNewOpDuration(parseInt(e.target.value) || 0)} className="flex-1 border rounded px-2 py-1 text-xs" />
                    <button onClick={async () => { if (newOpName && newOpDuration > 0) { await onAddOperation(table.id, newOpName, newOpDuration); setNewOpName(''); setNewOpDuration(0); setAddingOpFor(null); } }} className="px-2 py-1 bg-emerald-500 text-white rounded text-xs">OK</button>
                    <button onClick={() => { setAddingOpFor(null); setNewOpName(''); setNewOpDuration(0); }} className="px-2 py-1 bg-slate-200 rounded text-xs">X</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button onClick={() => setAddingOpFor(table.id)} className="flex-1 text-xs text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded py-1" data-testid={`add-prep-op-${table.table_number}`}><Plus className="w-3 h-3 inline" /> Add Op</button>
                  {(table.operations?.length > 0 || table.assigned_personnel_id) && (
                    <button onClick={() => { if (window.confirm('Clear this table?')) onClear(table.id); }} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50" data-testid={`clear-prep-table-${table.table_number}`}><Eraser className="w-3 h-3 inline" /></button>
                  )}
                </div>
              ))}
              {/* Overtime + Total time */}
              <div className="mt-1 flex items-center justify-between">
                <select value={prepTableOvertime[table.id] || 0}
                  onChange={e => setPrepTableOvertime(table.id, Number(e.target.value))}
                  disabled={isSimLocked}
                  className="border border-amber-300 bg-amber-50 rounded px-1.5 py-0.5 text-[10px] text-amber-700" data-testid={`overtime-prep-${table.table_number}`}>
                  <option value={0}>OT: 0</option><option value={30}>OT: 30dk</option><option value={60}>OT: 1h</option>
                  <option value={90}>OT: 1.5h</option><option value={120}>OT: 2h</option>
                </select>
                {table.total_time_sec > 0 && (
                  <span className="text-xs font-mono text-violet-600">{Math.floor(table.total_time_sec / 60)}:{(table.total_time_sec % 60).toString().padStart(2, '0')}</span>
                )}
              </div>
            </div>
          ))}
          {!isSimLocked && prepTables.length === 0 && (
            <div className="w-full text-center py-8 text-slate-400 text-sm">
              No prep tables yet. Click "Add Table" to create one.
            </div>
          )}
        </div>
      )}

      <Modal isOpen={personnelModalOpen} onClose={() => { setPersonnelModalOpen(false); setPersonnelSearch(''); }} title={`Assign Personnel - ${selectedTable?.name}`}>
        <div className="mb-3">
          <input type="text" placeholder="Search by name..." value={personnelSearch} onChange={e => setPersonnelSearch(e.target.value)}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="prep-personnel-search" autoFocus />
        </div>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {personnel.filter(p => p.name.toLowerCase().includes(personnelSearch.toLowerCase())).map((person) => {
            const assignment = personnelAssignments[person.id];
            const isOnLine = assignment?.type === 'line';
            const isOnTable = assignment?.type === 'table';
            const isOnThisTable = isOnTable && assignment?.table === selectedTable?.name;
            const isBlocked = isOnLine || (isOnTable && !isOnThisTable);
            let badge = null;
            if (isOnLine) badge = <span className="ml-auto text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded">{assignment.line} St.{assignment.stations?.join(',')}</span>;
            else if (isOnTable && !isOnThisTable) badge = <span className="ml-auto text-[10px] bg-orange-600 text-white px-1.5 py-0.5 rounded">{assignment.table}</span>;
            else if (isOnThisTable) badge = <span className="ml-auto text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">Already here</span>;
            return (
              <Button key={person.id} variant="secondary" className={`w-full justify-start text-left ${(isBlocked || isOnThisTable) ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={isBlocked || isOnThisTable}
                onClick={async () => { 
                  if (!isBlocked && !isOnThisTable && selectedTable) { 
                    try {
                      await onAssignPersonnel(selectedTable.id, person.id, 'add'); 
                      setPersonnelModalOpen(false); setPersonnelSearch(''); 
                    } catch (err) { 
                      const detail = err?.response?.data?.detail;
                      if (detail) alert(detail);
                    }
                  } 
                }}>
                <Users className="w-4 h-4 mr-2" /><span className="font-medium">{person.name}</span>
                <span className="ml-2 text-slate-500">({person.employee_id})</span>
                {badge}
              </Button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// PACKAGING AREA
// ============================================================
function PackagingArea({ lineResults, lines, prepTables }) {
  const { lineSimCompleted, prepTableSimCompleted } = useAppStore();
  
  const totalLineCompleted = Object.keys(lineResults).reduce((s, lineId) => s + (lineSimCompleted[lineId] || 0), 0);
  const totalPrepCompleted = Object.values(prepTableSimCompleted).reduce((s, c) => s + c, 0);
  const totalCompleted = totalLineCompleted + totalPrepCompleted;
  if (totalCompleted === 0) return null;

  const lineStyles = {
    'C1': { border: 'border-blue-400', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600', badge: 'bg-blue-100 border-blue-300 text-blue-700', borderB: 'border-blue-200' },
    'C2': { border: 'border-purple-400', bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-600', badge: 'bg-purple-100 border-purple-300 text-purple-700', borderB: 'border-purple-200' },
    'C3': { border: 'border-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', badge: 'bg-emerald-100 border-emerald-300 text-emerald-700', borderB: 'border-emerald-200' },
  };
  const defaultStyle = { border: 'border-slate-400', bg: 'bg-slate-50', text: 'text-slate-700', icon: 'text-slate-600', badge: 'bg-slate-100 border-slate-300 text-slate-700', borderB: 'border-slate-200' };
  const prepStyle = { border: 'border-violet-400', bg: 'bg-violet-50', text: 'text-violet-700', icon: 'text-violet-600', badge: 'bg-violet-100 border-violet-300 text-violet-700', borderB: 'border-violet-200' };

  return (
    <div className="space-y-4" data-testid="packaging-area">
      <div className="flex items-center gap-2">
        <Package className="w-5 h-5 text-emerald-600" />
        <h3 className="font-heading font-bold text-lg text-slate-800">Packaging Area</h3>
        <Badge variant="success" className="ml-2">{totalCompleted} Total Completed</Badge>
      </div>

      {/* Production Lines Packaging */}
      {totalLineCompleted > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Production Lines</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(lineResults).map(([lineId, result]) => {
              const simCount = lineSimCompleted[lineId] || 0;
              if (simCount === 0) return null;
              const s = lineStyles[result.line_code] || defaultStyle;
              return (
                <div key={lineId} className={`border-2 ${s.border} rounded-xl p-3 ${s.bg}`} data-testid={`packaging-${result.line_code}`}>
                  <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${s.borderB}`}>
                    <Package className={`w-4 h-4 ${s.icon}`} />
                    <span className="font-bold text-sm">{result.line_code}</span>
                    <span className={`text-xs font-mono font-bold ${s.text} ml-auto`}>{simCount} units</span>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {Array.from({ length: Math.min(simCount, 40) }, (_, i) => (
                      <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.015 }}
                        className={`px-1 py-0.5 ${s.badge} border rounded text-[7px] font-bold`}>
                        PRD{i + 1}
                      </motion.div>
                    ))}
                    {simCount > 40 && <span className="text-[10px] text-slate-400 self-center ml-1">+{simCount - 40} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prep Tables Packaging */}
      {totalPrepCompleted > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Production Tables</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {prepTables.filter(t => (prepTableSimCompleted[t.id] || 0) > 0).map(table => {
              const count = prepTableSimCompleted[table.id] || 0;
              return (
                <div key={table.id} className={`border-2 ${prepStyle.border} rounded-xl p-3 ${prepStyle.bg}`} data-testid={`packaging-prep-${table.table_number}`}>
                  <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${prepStyle.borderB}`}>
                    <Table2 className={`w-4 h-4 ${prepStyle.icon}`} />
                    <span className="font-bold text-xs">{table.name}</span>
                    <span className={`text-xs font-mono font-bold ${prepStyle.text} ml-auto`}>{count}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                    {Array.from({ length: Math.min(count, 20) }, (_, i) => (
                      <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.02 }}
                        className={`px-1 py-0.5 ${prepStyle.badge} border rounded text-[7px] font-bold`}>
                        PT{i + 1}
                      </motion.div>
                    ))}
                    {count > 20 && <span className="text-[10px] text-slate-400 self-center ml-1">+{count - 20} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// EXPORT MODAL
// ============================================================
function ExportModal({ isOpen, onClose, lines, stations, prepTables, lineActiveState, prepTablesActive, lineResults, personnel }) {
  const [downloading, setDownloading] = useState(false);
  const { shifts, prepTableOvertime } = useAppStore();
  if (!isOpen) return null;
  const activeLines = lines.filter(l => l.line_type !== 'table' && (lineActiveState[l.id] ?? true));
  const activeTables = prepTablesActive ? prepTables.filter(t => t.is_active) : [];
  const activeShift = shifts.length > 0 ? shifts[0] : null;

  const fmtMin = (m) => { if (!m) return '0m'; const h = Math.floor(m / 60); const mm = m % 60; return mm ? (h ? `${h}h ${mm}m` : `${mm}m`) : `${h}h`; };
  const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const buildShiftHTML = () => {
    if (!activeShift) return '';
    const breaks = (activeShift.breaks || []);
    const totalBreaks = breaks.reduce((s, b) => s + b.duration_min, 0);
    const gross = activeShift.gross_minutes || (activeShift.net_work_minutes + (activeShift.total_non_productive_min || 0));
    return `
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-bottom:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;"><strong>${activeShift.name}</strong><span style="color:#64748b;font-size:12px;">${activeShift.start_time} - ${activeShift.end_time}</span></div>
        <div style="display:flex;gap:12px;font-size:11px;">
          <span style="background:#fef3c7;padding:3px 8px;border-radius:4px;">Setup: ${activeShift.line_setup_min || 0}m</span>
          ${breaks.map(b => `<span style="background:#fed7aa;padding:3px 8px;border-radius:4px;">${b.name}: ${b.duration_min}m</span>`).join('')}
          <span style="background:#dbeafe;padding:3px 8px;border-radius:4px;">Cleanup: ${activeShift.line_cleanup_min || 0}m</span>
          <span style="background:#d1fae5;padding:3px 8px;border-radius:4px;font-weight:bold;">NET: ${fmtMin(activeShift.net_work_minutes)}</span>
          <span style="color:#94a3b8;">Gross: ${fmtMin(gross)}</span>
        </div>
      </div>`;
  };

  const openPrintWindow = () => {
    const shiftHTML = buildShiftHTML();
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const totalProduction = Object.values(lineResults).reduce((s, r) => s + r.daily_production, 0);

    let pages = [];

    // Page 1: Summary
    let summaryHTML = `
      <div class="page">
        <div style="text-align:center;margin-bottom:20px;border-bottom:3px solid #1e293b;padding-bottom:16px;">
          <h1 style="font-size:28px;margin:0;color:#1e293b;">PRODUCTION FLOOR PLAN</h1>
          <p style="font-size:14px;color:#64748b;margin:4px 0;">Kiel Americas Management | ${date}</p>
          <p style="font-size:13px;color:#475569;">${activeLines.length} Active Lines | ${activeTables.length} Prep Tables | ${personnel.filter(p => p.assigned_to).length} Personnel Assigned</p>
        </div>
        ${shiftHTML}
        ${totalProduction > 0 ? `
        <div style="background:#065f46;color:white;border-radius:8px;padding:16px;text-align:center;margin:16px 0;">
          <div style="font-size:12px;opacity:.7;">TOTAL DAILY PRODUCTION</div>
          <div style="font-size:36px;font-weight:bold;">${totalProduction} units/day</div>
        </div>` : ''}
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead><tr style="background:#1e293b;color:white;">
            <th style="padding:8px;text-align:left;">Line</th><th style="padding:8px;text-align:center;">Stations</th>
            <th style="padding:8px;text-align:center;">Personnel</th><th style="padding:8px;text-align:center;">Bottleneck</th>
            <th style="padding:8px;text-align:center;">Production</th><th style="padding:8px;text-align:center;">Efficiency</th>
          </tr></thead>
          <tbody>${activeLines.map((line, i) => {
            const ls = stations.filter(s => s.line_id === line.id);
            const assigned = ls.filter(s => s.assigned_personnel_name).length;
            const r = lineResults[line.id];
            const bottleneck = ls.length > 0 ? Math.max(...ls.map(s => s.total_time_sec || 0)) : 0;
            return `<tr style="background:${i % 2 === 0 ? '#f8fafc' : 'white'};border-bottom:1px solid #e2e8f0;">
              <td style="padding:8px;font-weight:bold;">${line.code} - ${line.name}</td>
              <td style="padding:8px;text-align:center;">${assigned}/${line.station_count}</td>
              <td style="padding:8px;text-align:center;">${assigned}</td>
              <td style="padding:8px;text-align:center;font-family:monospace;">${fmtSec(bottleneck)}</td>
              <td style="padding:8px;text-align:center;font-weight:bold;color:#059669;">${r ? r.daily_production : '-'}</td>
              <td style="padding:8px;text-align:center;">${r ? r.line_efficiency + '%' : '-'}</td>
            </tr>`;
          }).join('')}</tbody>
        </table>
        ${activeTables.length > 0 ? `
        <h3 style="margin-top:20px;font-size:16px;color:#7c3aed;">Production Tables</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead><tr style="background:#7c3aed;color:white;">
            <th style="padding:6px 8px;text-align:left;">Table</th><th style="padding:6px 8px;text-align:left;">Personnel</th>
            <th style="padding:6px 8px;text-align:left;">Operations</th><th style="padding:6px 8px;text-align:right;">Time</th>
          </tr></thead>
          <tbody>${activeTables.map((t, i) => `<tr style="background:${i % 2 === 0 ? '#faf5ff' : 'white'};border-bottom:1px solid #e2e8f0;">
            <td style="padding:6px 8px;font-weight:bold;">${t.name}</td>
            <td style="padding:6px 8px;">${(t.assigned_personnel || []).map(p => p.name).join(', ') || t.assigned_personnel_name || '-'}</td>
            <td style="padding:6px 8px;">${(t.operations || []).map(o => `${o.name} (${o.duration_sec}s)`).join(', ') || '-'}</td>
            <td style="padding:6px 8px;text-align:right;font-family:monospace;">${t.total_time_sec > 0 ? fmtSec(t.total_time_sec) : '-'}</td>
          </tr>`).join('')}</tbody>
        </table>` : ''}
      </div>`;
    pages.push(summaryHTML);

    // One page per active line
    activeLines.forEach(line => {
      const ls = stations.filter(s => s.line_id === line.id).sort((a, b) => a.station_number - b.station_number);
      const r = lineResults[line.id];
      const bottleneck = Math.max(...ls.map(s => s.total_time_sec || 0), 0);
      let lineHTML = `
        <div class="page">
          <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #1e293b;padding-bottom:8px;margin-bottom:12px;">
            <div>
              <h2 style="margin:0;font-size:22px;color:#1e293b;">${line.code} - ${line.name}</h2>
              <p style="font-size:12px;color:#64748b;margin:2px 0;">${line.station_count} Stations | Project: ${line.project_name || '-'} | Bottleneck: ${fmtSec(bottleneck)}</p>
            </div>
            ${r ? `<div style="background:#065f46;color:white;border-radius:8px;padding:8px 16px;text-align:center;">
              <div style="font-size:24px;font-weight:bold;">${r.daily_production}</div>
              <div style="font-size:10px;opacity:.8;">units/day | ${r.line_efficiency}% eff</div>
            </div>` : ''}
          </div>
          ${shiftHTML}
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <thead><tr style="background:#1e293b;color:white;">
              <th style="padding:6px 8px;width:50px;text-align:center;">St#</th>
              <th style="padding:6px 8px;text-align:left;width:180px;">Personnel</th>
              <th style="padding:6px 8px;text-align:left;">Operations</th>
              <th style="padding:6px 8px;text-align:right;width:70px;">Time</th>
              <th style="padding:6px 8px;text-align:center;width:60px;">Status</th>
            </tr></thead>
            <tbody>${ls.map((st, i) => {
              const hasPersonnel = !!st.assigned_personnel_name;
              const hasOps = st.assigned_operations?.length > 0;
              const isBN = st.total_time_sec === bottleneck && bottleneck > 0;
              return `<tr style="background:${isBN ? '#fef2f2' : i % 2 === 0 ? '#f8fafc' : 'white'};border-bottom:1px solid #e2e8f0;">
                <td style="padding:6px 8px;text-align:center;font-weight:bold;">St.${st.station_number}</td>
                <td style="padding:6px 8px;${hasPersonnel ? '' : 'color:#cbd5e1;font-style:italic;'}">${st.assigned_personnel_name || 'empty'}</td>
                <td style="padding:6px 8px;">${hasOps ? st.assigned_operations.map(op => `<span style="background:#f1f5f9;padding:2px 6px;border-radius:3px;margin-right:4px;font-size:10px;">${op.name} <span style="color:#64748b;">(${fmtSec(op.duration_sec)})</span></span>`).join('') : '<span style="color:#cbd5e1;">---</span>'}</td>
                <td style="padding:6px 8px;text-align:right;font-family:monospace;${isBN ? 'color:#dc2626;font-weight:bold;' : ''}">${st.total_time_sec > 0 ? fmtSec(st.total_time_sec) : '---'}</td>
                <td style="padding:6px 8px;text-align:center;">${hasPersonnel && hasOps ? '<span style="color:#059669;">&#10003;</span>' : hasPersonnel ? '<span style="color:#d97706;">&#9679;</span>' : '<span style="color:#cbd5e1;">&#9675;</span>'}</td>
              </tr>`;
            }).join('')}</tbody>
          </table>
        </div>`;
      pages.push(lineHTML);
    });

    // Page for prep tables (if any)
    if (activeTables.length > 0) {
      let ptHTML = `
        <div class="page">
          <div style="border-bottom:2px solid #7c3aed;padding-bottom:8px;margin-bottom:12px;">
            <h2 style="margin:0;font-size:22px;color:#7c3aed;">Production Tables</h2>
            <p style="font-size:12px;color:#64748b;">${activeTables.length} Active Tables</p>
          </div>
          ${shiftHTML}
          <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:16px;">
          ${activeTables.map(t => {
            const ot = prepTableOvertime[t.id] || 0;
            return `<div style="border:2px solid #c4b5fd;border-radius:8px;overflow:hidden;">
              <div style="background:#7c3aed;color:white;padding:8px 12px;font-weight:bold;">${t.name}${ot > 0 ? ` <span style="font-size:11px;opacity:.7;">+${ot}m OT</span>` : ''}</div>
              <div style="padding:12px;">
                <p style="font-size:11px;color:#64748b;margin:0 0 8px;">Personnel: <strong>${(t.assigned_personnel || []).map(p => p.name).join(', ') || t.assigned_personnel_name || '-'}</strong></p>
                ${(t.operations || []).length > 0 ? `<table style="width:100%;border-collapse:collapse;font-size:11px;">
                  <thead><tr style="background:#f5f3ff;"><th style="padding:4px 6px;text-align:left;">Operation</th><th style="padding:4px 6px;text-align:right;">Time</th></tr></thead>
                  <tbody>${t.operations.map((op, j) => `<tr style="border-bottom:1px solid #e2e8f0;"><td style="padding:4px 6px;">${op.name}</td><td style="padding:4px 6px;text-align:right;font-family:monospace;">${fmtSec(op.duration_sec)}</td></tr>`).join('')}</tbody>
                </table>` : '<p style="color:#cbd5e1;font-size:12px;">No operations</p>'}
                <div style="margin-top:8px;text-align:right;font-weight:bold;font-family:monospace;color:#7c3aed;">Total: ${t.total_time_sec > 0 ? fmtSec(t.total_time_sec) : '0:00'}</div>
              </div>
            </div>`;
          }).join('')}
          </div>
        </div>`;
      pages.push(ptHTML);
    }

    // Open print window
    const printHTML = `<!DOCTYPE html><html><head><title>Production Floor Plan</title>
      <style>
        @page { size: landscape; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e293b; }
        .page { page-break-after: always; padding: 0; min-height: calc(100vh - 40px); }
        .page:last-child { page-break-after: avoid; }
        .page-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 10px; color: #94a3b8; padding: 4px; }
        table { border: 1px solid #e2e8f0; }
        th, td { border: 1px solid #e2e8f0; }
        @media screen {
          body { background: #f1f5f9; padding: 20px; }
          .page { background: white; max-width: 1100px; margin: 0 auto 30px; padding: 30px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,.1); min-height: auto; }
          .no-print { position: fixed; top: 20px; right: 20px; z-index: 99; display: flex; gap: 8px; }
          .no-print button { padding: 10px 24px; border: none; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; }
          .btn-print { background: #1e293b; color: white; }
          .btn-pdf { background: #059669; color: white; }
          .btn-close { background: #ef4444; color: white; }
        }
        @media print { .no-print { display: none !important; } }
      </style></head><body>
      <div class="no-print">
        <button class="btn-print" onclick="window.print()">Print</button>
        <button class="btn-pdf" onclick="window.print()">Save as PDF</button>
        <button class="btn-close" onclick="window.close()">Close</button>
      </div>
      ${pages.join('\n')}
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(printHTML);
    win.document.close();
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API_URL}/api/export-day-report`);
      const report = res.data;
      const W = 70;
      let txt = '';
      txt += `${'#'.repeat(W)}\n`;
      txt += `#${' PRODUCTION DAY REPORT'.padEnd(W - 2)}#\n`;
      txt += `#${` ${new Date(report.generated_at).toLocaleString()}`.padEnd(W - 2)}#\n`;
      txt += `${'#'.repeat(W)}\n\n`;
      if (report.shift) {
        const shiftDoc = shifts.find(s => s.name === report.shift.name) || {};
        txt += `SHIFT: ${report.shift.name} (${report.shift.start} - ${report.shift.end})\n`;
        txt += `  Setup: ${shiftDoc.line_setup_min || 0}m | Cleanup: ${shiftDoc.line_cleanup_min || 0}m`;
        if (shiftDoc.breaks?.length) txt += ` | Breaks: ${shiftDoc.breaks.map(b => `${b.name} ${b.duration_min}m`).join(', ')}`;
        txt += `\n  NET WORK: ${fmtMin(report.shift.net_work_minutes)}\n\n`;
      }
      for (const line of report.lines) {
        txt += `${'═'.repeat(W)}\n`;
        txt += `${line.code} - ${line.name} | ${line.active_stations}/${line.station_count} stations\n`;
        txt += `${'─'.repeat(W)}\n`;
        txt += `${'St#'.padEnd(5)} ${'Personnel'.padEnd(22)} ${'Operations'.padEnd(30)} ${'Time'.padStart(7)}\n`;
        txt += `${'─'.repeat(W)}\n`;
        for (const st of line.stations) {
          if (st.is_empty && !st.personnel) continue;
          txt += `${String(st.station_number).padStart(3).padEnd(5)} ${(st.personnel || '-').padEnd(22)} ${(st.operations.join(', ') || '-').substring(0, 30).padEnd(30)} ${(st.total_time_sec > 0 ? fmtSec(st.total_time_sec) : '-').padStart(7)}\n`;
        }
      }
      if (report.prep_tables.length) {
        txt += `\n${'═'.repeat(W)}\nPREPARATION TABLES\n${'─'.repeat(W)}\n`;
        for (const t of report.prep_tables) {
          txt += `${t.name}: ${t.operations.join(', ') || '-'} | ${t.total_time_sec > 0 ? fmtSec(t.total_time_sec) : '-'} | ${t.personnel.join(', ') || '-'}\n`;
        }
      }
      if (Object.keys(lineResults).length > 0) {
        txt += `\n${'═'.repeat(W)}\nPRODUCTION OUTPUT\n${'─'.repeat(W)}\n`;
        for (const [, r] of Object.entries(lineResults)) txt += `  ${r.line_code.padEnd(10)} ${String(r.daily_production).padStart(4)} units/day | ${r.line_efficiency}% eff\n`;
        txt += `  ${'─'.repeat(W - 4)}\n  TOTAL: ${Object.values(lineResults).reduce((s, r) => s + r.daily_production, 0)} units/day\n`;
      }
      const blob = new Blob([txt], { type: 'text/plain' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `production-report-${new Date().toISOString().split('T')[0]}.txt`; a.click();
    } catch (err) { console.error(err); alert('Download failed'); }
    setDownloading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Production Plan">
      <div className="space-y-4">
        <div className="text-center border-b pb-3">
          <h2 className="text-xl font-bold text-slate-900">Production Floor Plan</h2>
          <p className="text-sm text-slate-500">{activeLines.length} Active Lines | {activeTables.length} Prep Tables</p>
        </div>

        {/* Quick Summary */}
        {activeShift && (
          <div className="bg-slate-50 rounded-lg p-3 border text-sm">
            <span className="font-bold">{activeShift.name}</span>
            <span className="text-slate-500 ml-2">{activeShift.start_time} - {activeShift.end_time}</span>
            <span className="ml-2 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">NET: {fmtMin(activeShift.net_work_minutes)}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {activeLines.map(line => {
            const ls = stations.filter(s => s.line_id === line.id);
            const assigned = ls.filter(s => s.assigned_personnel_name).length;
            const r = lineResults[line.id];
            return (
              <div key={line.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm">{line.code}</h4>
                  {r && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-bold">{r.daily_production} u/d</span>}
                </div>
                <p className="text-[10px] text-slate-500">{assigned}/{line.station_count} stations assigned</p>
              </div>
            );
          })}
        </div>

        {Object.keys(lineResults).length > 0 && (
          <div className="bg-emerald-600 text-white rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{Object.values(lineResults).reduce((s, r) => s + r.daily_production, 0)} units/day</p>
            <p className="text-xs opacity-75">Total Daily Production</p>
          </div>
        )}
      </div>
      <div className="mt-4 flex gap-2 justify-end border-t pt-3">
        <Button variant="secondary" onClick={handleDownloadReport} disabled={downloading} data-testid="download-report-btn">
          <Download className="w-4 h-4" /> {downloading ? 'Downloading...' : 'Report (.txt)'}
        </Button>
        <Button variant="primary" onClick={openPrintWindow} data-testid="print-export-btn">
          <Printer className="w-4 h-4" /> Print / PDF
        </Button>
      </div>
    </Modal>
  );
}

// ============================================================
// TEMPLATE MODAL - Line-based with Category/Customer/SeatType
// ============================================================
function TemplateModal({ isOpen, onClose, context, onTemplateApplied }) {
  const { templates, fetchTemplates, saveTemplate, deleteTemplate, lines, stations, fetchStations, categories, customers, seatTypes } = useAppStore();
  const [mode, setMode] = useState('list');
  const [tplName, setTplName] = useState('');
  const [tplCategory, setTplCategory] = useState('');
  const [tplCustomer, setTplCustomer] = useState('');
  const [tplSeatType, setTplSeatType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setMode(context?.mode === 'save' ? 'save' : 'list');
      setTplName(''); 
      setTplCategory(context?.defaultCategory || ''); 
      setTplCustomer(context?.defaultCustomer || ''); 
      setTplSeatType(context?.defaultSeatType || '');
    }
  }, [isOpen]);

  const line = lines.find(l => l.id === context?.lineId);
  const lineType = line ? `${line.station_count}-station` : '';
  const appliedTplId = context?.appliedTemplateId;
  const appliedTpl = appliedTplId ? templates.find(t => t.id === appliedTplId) : null;

  // Filter templates by station count (20-station for C1/C2, 7-station for C3)
  const filteredTemplates = templates
    .filter(t => t.line_type === lineType || !lineType)
    .filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.category || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.customer || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.seat_type || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const handleApply = async (templateId) => {
    if (!context?.lineId) return;
    if (!window.confirm('Apply this template? Current operations will be replaced.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/templates/${templateId}/apply`, { line_id: context.lineId });
      await fetchStations(context.lineId);
      if (context?.onTemplateApplied) {
        context.onTemplateApplied({ ...res.data, templateId });
      }
      onClose();
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!tplName || !context?.lineId) return;
    if (!window.confirm(`Save as "${tplName}"?`)) return;
    try {
      await saveTemplate({ 
        name: tplName, line_id: context.lineId,
        category: tplCategory, customer: tplCustomer, seat_type: tplSeatType 
      });
      setTplName(''); setTplCategory(''); setTplCustomer(''); setTplSeatType('');
      fetchTemplates(); setMode('list');
    } catch (err) { console.error(err); }
  };

  const handleOverwrite = async () => {
    if (!appliedTplId || !context?.lineId) return;
    if (!window.confirm(`Overwrite template "${appliedTpl?.name}"?`)) return;
    try {
      await axios.put(`${API_URL}/api/templates/${appliedTplId}`, {
        line_id: context.lineId,
        category: tplCategory || appliedTpl?.category,
        customer: tplCustomer || appliedTpl?.customer,
        seat_type: tplSeatType || appliedTpl?.seat_type,
      });
      fetchTemplates(); setMode('list');
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    try { await deleteTemplate(templateId); fetchTemplates(); } catch (err) { console.error(err); }
  };

  if (!isOpen) return null;

  const filteredCust = tplCategory ? customers.filter(c => {
    const cat = categories.find(ct => ct.name === tplCategory);
    return cat && c.category_id === cat.id;
  }) : customers;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Templates${line ? ` - ${line.code} (${lineType})` : ''}`}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button variant={mode === 'list' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('list')} data-testid="tpl-use-existing">
            <FolderOpen className="w-3 h-3" /> Use Existing
          </Button>
          <Button variant={mode === 'save' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('save')} data-testid="tpl-create-new">
            <FilePlus className="w-3 h-3" /> Create New
          </Button>
        </div>

        {mode === 'list' && (
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input type="text" placeholder="Search by name, category, customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="template-search" />
            </div>
            <p className="text-xs text-slate-500">Showing {lineType} compatible templates</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredTemplates.length > 0 ? filteredTemplates.map(tpl => (
                <div key={tpl.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{tpl.name}</p>
                    <div className="flex gap-2 text-xs text-slate-500 mt-0.5">
                      {tpl.category && <span className="bg-blue-50 text-blue-600 px-1.5 rounded">{tpl.category}</span>}
                      {tpl.customer && <span className="bg-emerald-50 text-emerald-600 px-1.5 rounded">{tpl.customer}</span>}
                      {tpl.seat_type && <span className="bg-amber-50 text-amber-600 px-1.5 rounded">{tpl.seat_type}</span>}
                      <span className="text-slate-400">{tpl.station_operations?.length || 0} stations with ops</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button variant="primary" size="sm" onClick={() => handleApply(tpl.id)}>Apply</Button>
                    <button onClick={() => handleDelete(tpl.id, tpl.name)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              )) : <p className="text-sm text-slate-400 text-center py-6">No templates found for {lineType}</p>}
            </div>
          </>
        )}

        {mode === 'save' && (
          <div className="space-y-3">
            {appliedTpl && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">Active template: <b>{appliedTpl.name}</b></p>
                <div className="flex gap-2">
                  <Button variant="primary" size="sm" onClick={handleOverwrite} data-testid="tpl-overwrite-btn">
                    <Save className="w-3 h-3" /> Overwrite "{appliedTpl.name}"
                  </Button>
                  <span className="text-xs text-amber-600 self-center">or create a new one below</span>
                </div>
              </div>
            )}
            <p className="text-sm text-slate-500">Save all operations from <b>{line?.code}</b> ({lineType}) as a reusable template.</p>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Category</label>
                <select value={tplCategory} onChange={e => { setTplCategory(e.target.value); setTplCustomer(''); setTplSeatType(''); }}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="tpl-category">
                  <option value="">Select...</option>
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Customer</label>
                <select value={tplCustomer} onChange={e => { setTplCustomer(e.target.value); setTplSeatType(''); }}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="tpl-customer">
                  <option value="">Select...</option>
                  {filteredCust.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Seat Type</label>
                <select value={tplSeatType} onChange={e => setTplSeatType(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="tpl-seat-type">
                  <option value="">Select...</option>
                  {seatTypes.filter(s => !tplCustomer || s.customer_id === customers.find(c => c.name === tplCustomer)?.id).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Template Name</label>
              <input type="text" placeholder={`e.g. ${tplCategory || 'Train'}-${tplCustomer || 'BART'}-${tplSeatType || 'ESOS'}-${lineType}`}
                value={tplName} onChange={e => setTplName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tpl-name-input" autoFocus />
            </div>

            {/* Auto-name suggestion */}
            {(tplCategory || tplCustomer || tplSeatType) && !tplName && (
              <button onClick={() => setTplName([tplCategory, tplCustomer, tplSeatType, lineType].filter(Boolean).join('-'))}
                className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                Auto-fill: {[tplCategory, tplCustomer, tplSeatType, lineType].filter(Boolean).join('-')}
              </button>
            )}

            <Button variant="primary" onClick={handleSave} disabled={!tplName} data-testid="tpl-save-btn">
              <Save className="w-4 h-4" /> Save as New Template
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Helper
function formatTime(seconds) {
  const s = Math.floor(seconds);
  const hrs = Math.floor(s / 3600); const mins = Math.floor((s % 3600) / 60); const secs = s % 60;
  if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default ProductionFloor;
